/**
 * Elite Batch 19 Part 3 — Workflow Templates 1788–1807
 * Field event booth scan processor, field size overflow detection, former boss
 * warm path, former colleague warm path, funding news trigger outreach, G2 profile
 * completion optimizer, geo-based lead routing, GitHub contributor recruiting,
 * Gmail rep mailbox send, Gong transcript next steps extractor, Google Maps local
 * TAM builder, guest post pitch, handwritten letter queue builder, high-value
 * account contact expansion, HubSpot contact upsert, HubSpot enrichment batch,
 * implementation multi-thread kickoff, inbound lead enrichment, Indeed job-posting
 * enrichment, Indeed jobs to email queue.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 19 PART 3 — TEMPLATES 1788–1807
// =============================================================================

export const ELITE_BATCH19_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1788. FIELD EVENT BOOTH SCAN PROCESSOR (9 nodes)
  // ===================================================================

  "Field Event Booth Scan Processor": {
    summary:
      "Fires when a badge scan occurs at the event booth via webhook, standardizes raw scan data with runCode, enriches the company via Clearbit API, deduplicates against existing CRM contacts in HubSpot, scores ICP interaction fit with runCode, routes by priority via condition, AI-drafts tiered follow-up messaging, batch-upserts to HubSpot CRM, and dispatches rep notification via Slack plus follow-up email.",
    nodes: [
      trigger("Webhook: Event booth badge scanned", "webhook_app", {
        source: "eventbrite",
        event: "attendee.checked_in",
        howItStarts:
          "Fires when a badge is scanned at the event booth, sending the raw attendee check-in data including badge ID, scan timestamp, and booth identifier",
        setup:
          "Configure event platform webhook for badge scan events. Set Clearbit API key for company enrichment. Set HubSpot API token for CRM dedup and upsert. Set OpenAI API key for follow-up drafting. Configure Slack webhook for rep notifications.",
        inputFields: [],
      }),

      // 2. AI extract scan data
      aiNode(
        "2",
        "AI Extract Booth Scan Data",
        280,
        "You are an event badge scan parser. Extract the attendee name, email, company, job title, phone, badge ID, booth ID, scan timestamp, event name, and any custom fields from the raw webhook payload. Respond JSON: {attendee_name, attendee_email, attendee_company, attendee_title, attendee_phone, badge_id, booth_id, scan_timestamp, event_name, registration_type}",
        "Raw webhook payload: {webhook_payload}. Extract all attendee check-in data regardless of field naming conventions.",
        [
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_company", as: "attendee_company", type: "string" },
          { field: "attendee_title", as: "attendee_title", type: "string" },
          { field: "attendee_phone", as: "attendee_phone", type: "string" },
          { field: "badge_id", as: "badge_id", type: "string" },
          { field: "booth_id", as: "booth_id", type: "string" },
          { field: "scan_timestamp", as: "scan_timestamp", type: "string" },
          { field: "event_name", as: "event_name", type: "string" },
          {
            field: "registration_type",
            as: "registration_type",
            type: "string",
          },
        ],
      ),

      // 3. Standardization runCode
      n("3", "runCode", "Standardize Scan Data and Normalize Fields", 520, {
        runCodeDetails: {
          code: `const name = (variables.attendee_name || '').trim();
const nameParts = name.split(' ');
const firstName = nameParts[0] || '';
const lastName = nameParts.slice(1).join(' ') || '';
const email = (variables.attendee_email || '').toLowerCase().trim();
const domain = email.split('@')[1] || '';
const company = (variables.attendee_company || '').trim();
const title = (variables.attendee_title || '').trim();
const phone = (variables.attendee_phone || '').replace(/[^0-9+]/g, '');
const isPersonalEmail = ['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com'].includes(domain);
const scanTime = new Date(variables.scan_timestamp || Date.now());
const dayOfEvent = scanTime.toISOString().split('T')[0];
output.first_name = firstName;
output.last_name = lastName;
output.clean_email = email;
output.email_domain = domain;
output.clean_company = company;
output.clean_title = title;
output.clean_phone = phone;
output.is_personal_email = isPersonalEmail;
output.scan_day = dayOfEvent;
output.lead_source = 'Event: ' + (variables.event_name || 'Unknown') + ' Booth: ' + (variables.booth_id || 'Unknown');`,
          outputVariables: [
            { name: "first_name", type: "string" },
            { name: "last_name", type: "string" },
            { name: "clean_email", type: "string" },
            { name: "email_domain", type: "string" },
            { name: "clean_company", type: "string" },
            { name: "clean_title", type: "string" },
            { name: "clean_phone", type: "string" },
            { name: "is_personal_email", type: "boolean" },
            { name: "scan_day", type: "string" },
            { name: "lead_source", type: "string" },
          ],
        },
      }),

      // 4. Company enrichment Clearbit API
      n("4", "hitApi", "Enrich Company via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={email_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "geo.country",
              customName: "company_country",
              type: "string",
            },
            { name: "tech", customName: "company_tech_stack", type: "string" },
          ],
        },
      }),

      // 5. CRM dedup HubSpot
      hubspotPostNode(
        "5",
        "Deduplicate Against HubSpot CRM",
        1000,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{clean_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","lifecyclestage","hs_lead_status"]',
          },
        ],
        [
          {
            name: "total",
            customName: "existing_contact_count",
            type: "number",
          },
          {
            name: "results[0].id",
            customName: "existing_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "existing_lifecycle",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "existing_lead_status",
            type: "string",
          },
        ],
      ),

      // 6. ICP interaction scoring runCode
      n("6", "runCode", "Score ICP Fit and Interaction Priority", 1240, {
        runCodeDetails: {
          code: `let score = 0;
const employees = parseInt(variables.company_employees || '0');
const revenue = parseInt(variables.company_revenue || '0');
const title = (variables.clean_title || '').toLowerCase();
const industry = (variables.company_industry || '').toLowerCase();
const isExisting = parseInt(variables.existing_contact_count || '0') > 0;
if (employees >= 100 && employees <= 5000) score += 25;
else if (employees > 5000) score += 15;
if (revenue >= 10000000) score += 20;
else if (revenue >= 1000000) score += 10;
if (title.match(/vp|director|head|chief|cto|cfo|ceo|founder/)) score += 30;
else if (title.match(/manager|lead|senior/)) score += 20;
else if (title.match(/analyst|coordinator|specialist/)) score += 10;
if (industry.match(/technology|software|saas|fintech|healthcare/)) score += 15;
if (!variables.is_personal_email) score += 10;
const priority = score >= 70 ? 'hot' : score >= 40 ? 'warm' : 'nurture';
const isNewLead = !isExisting;
output.icp_score = score;
output.lead_priority = priority;
output.is_new_lead = isNewLead;
output.score_breakdown = 'Size:' + employees + ' Rev:' + revenue + ' Title:' + title + ' Industry:' + industry;`,
          outputVariables: [
            { name: "icp_score", type: "number" },
            { name: "lead_priority", type: "string" },
            { name: "is_new_lead", type: "boolean" },
            { name: "score_breakdown", type: "string" },
          ],
        },
      }),

      // 7. Priority routing condition
      n("7", "condition", "Route by Lead Priority Level", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "lead_priority", operator: "equals", value: "hot" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. AI tiered follow-up draft
      aiNode(
        "8",
        "AI Draft Tiered Follow-Up Message",
        1720,
        "You are an expert event follow-up copywriter. Based on the lead priority, ICP score, and enrichment data, craft a personalized follow-up email. For hot leads: urgent, reference specific pain points for their industry and role. For warm leads: value-driven, share relevant case study. For nurture leads: educational content offer. Return JSON: {subject_line, email_body, follow_up_cadence, recommended_cta, personalization_notes}",
        "Attendee: {first_name} {last_name} | Title: {clean_title} | Company: {enriched_company_name} | Industry: {company_industry} | Employees: {company_employees} | Priority: {lead_priority} | ICP Score: {icp_score} | Event: {event_name} | Is New: {is_new_lead}",
        [
          { field: "subject_line", as: "followup_subject", type: "string" },
          { field: "email_body", as: "followup_body", type: "string" },
          {
            field: "follow_up_cadence",
            as: "followup_cadence",
            type: "string",
          },
          { field: "recommended_cta", as: "followup_cta", type: "string" },
          {
            field: "personalization_notes",
            as: "personalization_notes",
            type: "string",
          },
        ],
        300,
      ),

      // 9. CRM batch upsert + rep notification
      hubspotPostNode(
        "9",
        "Upsert Event Lead to HubSpot CRM",
        1960,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{clean_email}","firstname":"{first_name}","lastname":"{last_name}","company":"{enriched_company_name}","jobtitle":"{clean_title}","phone":"{clean_phone}","lifecyclestage":"lead","hs_lead_status":"{lead_priority}","lead_source":"{lead_source}","industry":"{company_industry}","numberofemployees":"{company_employees}","notes_last_updated":"Event scan: {event_name}. ICP Score: {icp_score}. Priority: {lead_priority}. Follow-up cadence: {followup_cadence}."}',
          },
        ],
        [
          { name: "id", customName: "upserted_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "confirmed_email",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1789. FIELD SIZE OVERFLOW DETECTION (6 nodes)
  // ===================================================================

  "Field Size Overflow Detection": {
    summary:
      "Runs daily to pull CRM schema field limits with runCode, scans HubSpot records for near-overflow fields, classifies overflow risk with runCode, assesses operational impact with runCode, and reports findings plus alerts via Slack and Google Sheets.",
    nodes: [
      trigger("Schedule: Daily CRM field overflow scan", "schedule", {
        schedule: "Every day at 6:00 AM",
        howItStarts:
          "Runs daily to detect CRM fields approaching their character or value limits, preventing data truncation, sync failures, and integration breakage before they impact operations",
        setup:
          "Configure HubSpot API token for schema and record queries. Set Google Sheets credentials for overflow reporting. Configure Slack webhook for critical alerts.",
        inputFields: [],
      }),

      // 2. Schema limits pull runCode
      n("2", "runCode", "Define CRM Field Size Limits", 280, {
        runCodeDetails: {
          code: `const fieldLimits = {
  'firstname': { maxLength: 255, type: 'text' },
  'lastname': { maxLength: 255, type: 'text' },
  'company': { maxLength: 500, type: 'text' },
  'jobtitle': { maxLength: 500, type: 'text' },
  'notes_last_updated': { maxLength: 65536, type: 'textarea' },
  'description': { maxLength: 65536, type: 'textarea' },
  'hs_email_domain': { maxLength: 255, type: 'text' },
  'phone': { maxLength: 50, type: 'text' },
  'address': { maxLength: 500, type: 'text' },
  'city': { maxLength: 255, type: 'text' },
};
const warningThreshold = 0.85;
const criticalThreshold = 0.95;
output.field_limits_json = JSON.stringify(fieldLimits);
output.warning_threshold = warningThreshold;
output.critical_threshold = criticalThreshold;
output.fields_to_check = Object.keys(fieldLimits).join(',');
output.total_fields_monitored = Object.keys(fieldLimits).length;`,
          outputVariables: [
            { name: "field_limits_json", type: "string" },
            { name: "warning_threshold", type: "number" },
            { name: "critical_threshold", type: "number" },
            { name: "fields_to_check", type: "string" },
            { name: "total_fields_monitored", type: "number" },
          ],
        },
      }),

      // 3. Record scan HubSpot
      hubspotPostNode(
        "3",
        "Scan HubSpot Records for Field Overflow",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"notes_last_updated","operator":"HAS_PROPERTY"}]}]',
          },
          {
            key: "properties",
            value:
              '["firstname","lastname","company","jobtitle","notes_last_updated","description","phone","address","city","hs_email_domain"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "results", customName: "scanned_records", type: "string" },
          {
            name: "total",
            customName: "total_records_scanned",
            type: "number",
          },
        ],
      ),

      // 4. Overflow classification runCode
      n("4", "runCode", "Classify Field Overflow Risk Levels", 760, {
        runCodeDetails: {
          code: `const limits = JSON.parse(variables.field_limits_json || '{}');
const records = JSON.parse(variables.scanned_records || '[]');
const warnThresh = variables.warning_threshold || 0.85;
const critThresh = variables.critical_threshold || 0.95;
let criticalFields = [];
let warningFields = [];
let safeCount = 0;
for (const rec of records.slice(0, 50)) {
  const props = rec.properties || {};
  for (const [field, config] of Object.entries(limits)) {
    const val = props[field] || '';
    const ratio = val.length / config.maxLength;
    if (ratio >= critThresh) {
      criticalFields.push(field + ':' + val.length + '/' + config.maxLength + ' (record ' + rec.id + ')');
    } else if (ratio >= warnThresh) {
      warningFields.push(field + ':' + val.length + '/' + config.maxLength + ' (record ' + rec.id + ')');
    } else {
      safeCount++;
    }
  }
}
output.critical_count = criticalFields.length;
output.warning_count = warningFields.length;
output.safe_count = safeCount;
output.critical_details = criticalFields.join('; ');
output.warning_details = warningFields.join('; ');
output.overall_risk = criticalFields.length > 0 ? 'critical' : warningFields.length > 0 ? 'warning' : 'healthy';`,
          outputVariables: [
            { name: "critical_count", type: "number" },
            { name: "warning_count", type: "number" },
            { name: "safe_count", type: "number" },
            { name: "critical_details", type: "string" },
            { name: "warning_details", type: "string" },
            { name: "overall_risk", type: "string" },
          ],
        },
      }),

      // 5. Impact assessment runCode
      n("5", "runCode", "Assess Operational Impact of Overflows", 1000, {
        runCodeDetails: {
          code: `const critCount = parseInt(variables.critical_count || '0');
const warnCount = parseInt(variables.warning_count || '0');
const totalScanned = parseInt(variables.total_records_scanned || '0');
const riskPct = totalScanned > 0 ? ((critCount + warnCount) / totalScanned * 100).toFixed(1) : '0';
let impact = 'low';
let recommendation = 'No action needed. All fields within safe limits.';
if (critCount > 10) {
  impact = 'severe';
  recommendation = 'Immediate action required: Archive old notes, migrate long-text fields to custom objects, or increase field limits. ' + critCount + ' fields at critical capacity.';
} else if (critCount > 0) {
  impact = 'high';
  recommendation = 'Schedule field cleanup within 48 hours. ' + critCount + ' fields approaching maximum. Consider automated note archival.';
} else if (warnCount > 5) {
  impact = 'moderate';
  recommendation = 'Monitor closely. ' + warnCount + ' fields approaching warning threshold. Plan proactive cleanup.';
}
const reportDate = new Date().toISOString().split('T')[0];
output.impact_level = impact;
output.recommendation = recommendation;
output.risk_percentage = riskPct;
output.report_date = reportDate;
output.report_summary = 'Overflow Report ' + reportDate + ': ' + critCount + ' critical, ' + warnCount + ' warning, ' + variables.safe_count + ' safe. Impact: ' + impact + '. Risk: ' + riskPct + '% of records.';`,
          outputVariables: [
            { name: "impact_level", type: "string" },
            { name: "recommendation", type: "string" },
            { name: "risk_percentage", type: "string" },
            { name: "report_date", type: "string" },
            { name: "report_summary", type: "string" },
          ],
        },
      }),

      // 6. Report + alert Slack Sheets
      slackNode(
        "6",
        "Alert Team and Log Overflow Report",
        1240,
        "CRM Field Overflow Detection Report\\n\\nDate: {report_date} | Overall Risk: {overall_risk} | Impact: {impact_level}\\nRecords Scanned: {total_records_scanned} | At-Risk: {risk_percentage}%\\n\\nCritical ({critical_count}): {critical_details}\\nWarning ({warning_count}): {warning_details}\\n\\nRecommendation: {recommendation}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1790. FORMER BOSS WARM PATH (8 nodes)
  // ===================================================================

  "Former Boss Warm Path": {
    summary:
      "Fires when a champion is identified at a target account via webhook, extracts career history with runCode, cross-references prospects in HubSpot, assesses relationship strength with runCode, generates a warm-intro strategy with runCode, AI-drafts the intro request, sends the request email and logs to Sheets, and sends the prospect outreach email.",
    nodes: [
      trigger("Webhook: Champion identified at target account", "webhook_app", {
        source: "hubspot",
        event: "contact.propertyChange",
        howItStarts:
          "Fires when a contact is tagged as a champion or advocate in the CRM, triggering analysis of their former boss relationships that could provide warm introductions to target accounts",
        setup:
          "Configure HubSpot webhook for contact property changes on champion tags. Set OpenAI API key for intro strategy drafting. Configure Google Sheets for tracking. Set email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract champion data
      aiNode(
        "2",
        "AI Extract Champion Details",
        280,
        "You are a CRM contact parser. Extract the champion name, email, current company, current title, LinkedIn URL, tagged reason, deal ID, target account name, target account domain, and the champion score from the raw webhook payload. Respond JSON: {champion_name, champion_email, current_company, current_title, linkedin_url, champion_reason, deal_id, target_account, target_domain, champion_score}",
        "Raw webhook payload: {webhook_payload}. Extract all champion identification data regardless of field naming conventions.",
        [
          { field: "champion_name", as: "champion_name", type: "string" },
          { field: "champion_email", as: "champion_email", type: "string" },
          {
            field: "current_company",
            as: "champion_current_company",
            type: "string",
          },
          {
            field: "current_title",
            as: "champion_current_title",
            type: "string",
          },
          { field: "linkedin_url", as: "champion_linkedin", type: "string" },
          { field: "champion_reason", as: "champion_reason", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "target_account", as: "target_account", type: "string" },
          { field: "target_domain", as: "target_domain", type: "string" },
          { field: "champion_score", as: "champion_score", type: "string" },
        ],
      ),

      // 3. Career-history extraction runCode
      n("3", "runCode", "Extract Career History and Boss Relationships", 520, {
        runCodeDetails: {
          code: `const name = variables.champion_name || '';
const currentCompany = variables.champion_current_company || '';
const currentTitle = variables.champion_current_title || '';
const targetAccount = variables.target_account || '';
const targetDomain = variables.target_domain || '';
const linkedinUrl = variables.champion_linkedin || '';
const hasDirectConnection = currentTitle.toLowerCase().includes('former') || currentCompany.toLowerCase() === targetAccount.toLowerCase();
const seniorityKeywords = ['vp','director','head','chief','svp','evp','president','founder'];
const isTargetSenior = seniorityKeywords.some(k => currentTitle.toLowerCase().includes(k));
output.champion_first_name = name.split(' ')[0] || name;
output.career_connection_type = hasDirectConnection ? 'direct_former_employer' : 'indirect_network';
output.target_seniority_match = isTargetSenior;
output.linkedin_profile = linkedinUrl;
output.research_query = name + ' ' + targetAccount + ' career history';
output.connection_hypothesis = name + ' may have reported to leaders at ' + targetAccount + ' based on career trajectory from ' + currentCompany;`,
          outputVariables: [
            { name: "champion_first_name", type: "string" },
            { name: "career_connection_type", type: "string" },
            { name: "target_seniority_match", type: "boolean" },
            { name: "linkedin_profile", type: "string" },
            { name: "research_query", type: "string" },
            { name: "connection_hypothesis", type: "string" },
          ],
        },
      }),

      // 4. Prospect cross-reference HubSpot
      hubspotPostNode(
        "4",
        "Cross-Reference Prospects at Target Account",
        760,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"company","operator":"CONTAINS_TOKEN","value":"{target_account}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","jobtitle","company","lifecyclestage","hs_lead_status","notes_last_contacted"]',
          },
          { key: "limit", value: "20" },
        ],
        [
          { name: "total", customName: "prospects_at_target", type: "number" },
          {
            name: "results[0].properties.email",
            customName: "top_prospect_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "top_prospect_first",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "top_prospect_last",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "top_prospect_title",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "top_prospect_id",
            type: "string",
          },
        ],
      ),

      // 5. Relationship-strength assessment runCode
      n("5", "runCode", "Assess Relationship Strength for Warm Intro", 1000, {
        runCodeDetails: {
          code: `const connectionType = variables.career_connection_type || '';
const prospectCount = parseInt(variables.prospects_at_target || '0');
const championScore = parseInt(variables.champion_score || '0');
const prospectTitle = (variables.top_prospect_title || '').toLowerCase();
let strengthScore = 0;
if (connectionType === 'direct_former_employer') strengthScore += 40;
else strengthScore += 15;
if (championScore >= 80) strengthScore += 25;
else if (championScore >= 50) strengthScore += 15;
if (prospectTitle.match(/vp|director|head|chief|cto|ceo/)) strengthScore += 20;
else if (prospectTitle.match(/manager|lead|senior/)) strengthScore += 10;
if (prospectCount > 0) strengthScore += 15;
const introViability = strengthScore >= 60 ? 'strong' : strengthScore >= 35 ? 'moderate' : 'weak';
output.relationship_strength = strengthScore;
output.intro_viability = introViability;
output.prospect_coverage = prospectCount;
output.warm_path_summary = 'Champion ' + variables.champion_name + ' (' + connectionType + ') -> ' + variables.top_prospect_first + ' ' + variables.top_prospect_last + ' at ' + variables.target_account + '. Strength: ' + strengthScore + '/100.';`,
          outputVariables: [
            { name: "relationship_strength", type: "number" },
            { name: "intro_viability", type: "string" },
            { name: "prospect_coverage", type: "number" },
            { name: "warm_path_summary", type: "string" },
          ],
        },
      }),

      // 6. Warm-intro strategy runCode
      n("6", "runCode", "Generate Warm Intro Strategy", 1240, {
        runCodeDetails: {
          code: `const viability = variables.intro_viability || 'weak';
const strength = parseInt(variables.relationship_strength || '0');
const prospectTitle = variables.top_prospect_title || '';
let approach = '';
let askType = '';
let timing = '';
if (viability === 'strong') {
  approach = 'Direct ask for a warm introduction. Reference shared history and mutual benefit. Position as reconnecting over industry trends.';
  askType = 'direct_intro';
  timing = 'Send within 24 hours while relationship context is fresh';
} else if (viability === 'moderate') {
  approach = 'Soft approach — ask champion to forward a brief note or make a LinkedIn introduction. Lower commitment ask.';
  askType = 'forward_request';
  timing = 'Send within 48 hours with a soft touch';
} else {
  approach = 'Build relationship first — schedule a catch-up call with champion before asking for intro. Nurture the connection.';
  askType = 'nurture_first';
  timing = 'Schedule champion catch-up within 1 week, then reassess intro ask';
}
output.intro_approach = approach;
output.ask_type = askType;
output.timing_recommendation = timing;
output.strategy_context = 'Viability: ' + viability + ' | Strength: ' + strength + ' | Prospect: ' + prospectTitle + ' | Ask: ' + askType;`,
          outputVariables: [
            { name: "intro_approach", type: "string" },
            { name: "ask_type", type: "string" },
            { name: "timing_recommendation", type: "string" },
            { name: "strategy_context", type: "string" },
          ],
        },
      }),

      // 7. AI intro request draft + Sheets logging
      aiNode(
        "7",
        "AI Draft Introduction Request Email",
        1480,
        "You are an expert relationship-based sales strategist. Draft a warm, genuine email from a sales rep to the champion asking for an introduction to the prospect at the target account. The tone should be appreciative, specific, and low-pressure. Reference the shared history. Return JSON: {intro_request_subject, intro_request_body, linkedin_note_variant, suggested_followup_if_no_response}",
        "Champion: {champion_name} ({champion_current_title} at {champion_current_company}) | Prospect: {top_prospect_first} {top_prospect_last} ({top_prospect_title} at {target_account}) | Connection: {career_connection_type} | Approach: {intro_approach} | Ask Type: {ask_type} | Context: {connection_hypothesis}",
        [
          {
            field: "intro_request_subject",
            as: "intro_subject",
            type: "string",
          },
          { field: "intro_request_body", as: "intro_body", type: "string" },
          {
            field: "linkedin_note_variant",
            as: "linkedin_note",
            type: "string",
          },
          {
            field: "suggested_followup_if_no_response",
            as: "followup_if_no_reply",
            type: "string",
          },
        ],
      ),

      // 8. Prospect outreach email
      emailNode(
        "8",
        "Send Intro Request to Champion",
        1720,
        "{champion_email}",
        "{intro_subject}",
        "{intro_body}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1791. FORMER COLLEAGUE WARM PATH (8 nodes)
  // ===================================================================

  "Former Colleague Warm Path": {
    summary:
      "Fires when a warm-path signal arrives via webhook, maps customer career history with runCode, detects employment overlaps with runCode, scores overlap quality with runCode, gates on relationship quality via condition, AI-generates a peer-intro strategy, drafts and sends the intro ask email, and tracks the outreach in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Warm path signal detected", "webhook_app", {
        source: "hubspot",
        event: "contact.propertyChange",
        howItStarts:
          "Fires when a customer contact is flagged as having a potential warm-path connection to a prospect, triggering peer-level introduction analysis based on shared employment history",
        setup:
          "Configure HubSpot webhook for contact property changes on warm-path flags. Set OpenAI API key for strategy generation. Configure email sending credentials. Set HubSpot token for CRM tracking.",
        inputFields: [],
      }),

      // 2. AI extract warm path data
      aiNode(
        "2",
        "AI Extract Warm Path Signal Data",
        280,
        "You are a CRM warm-path parser. Extract the customer name, customer email, customer company, customer title, customer LinkedIn, prospect name, prospect email, prospect company, prospect title, prospect LinkedIn, and the connection signal source from the webhook payload. Respond JSON: {customer_name, customer_email, customer_company, customer_title, customer_linkedin, prospect_name, prospect_email, prospect_company, prospect_title, prospect_linkedin, signal_source}",
        "Raw webhook payload: {webhook_payload}. Extract all warm-path signal data regardless of field naming conventions.",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_company", as: "customer_company", type: "string" },
          { field: "customer_title", as: "customer_title", type: "string" },
          {
            field: "customer_linkedin",
            as: "customer_linkedin",
            type: "string",
          },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "prospect_title", as: "prospect_title", type: "string" },
          {
            field: "prospect_linkedin",
            as: "prospect_linkedin",
            type: "string",
          },
          { field: "signal_source", as: "signal_source", type: "string" },
        ],
      ),

      // 3. Customer career mapping runCode
      n("3", "runCode", "Map Customer Career History for Overlaps", 520, {
        runCodeDetails: {
          code: `const custCompany = (variables.customer_company || '').toLowerCase();
const prospCompany = (variables.prospect_company || '').toLowerCase();
const custTitle = (variables.customer_title || '').toLowerCase();
const prospTitle = (variables.prospect_title || '').toLowerCase();
const custName = variables.customer_name || '';
const prospName = variables.prospect_name || '';
const custLinkedin = variables.customer_linkedin || '';
const prospLinkedin = variables.prospect_linkedin || '';
const sameIndustryKeywords = ['tech','software','saas','fintech','healthcare','ai','cloud'];
const custIndustryMatch = sameIndustryKeywords.filter(k => custCompany.includes(k) || custTitle.includes(k));
const prospIndustryMatch = sameIndustryKeywords.filter(k => prospCompany.includes(k) || prospTitle.includes(k));
const industryOverlap = custIndustryMatch.filter(k => prospIndustryMatch.includes(k)).length > 0;
output.customer_first_name = custName.split(' ')[0] || custName;
output.prospect_first_name = prospName.split(' ')[0] || prospName;
output.industry_overlap = industryOverlap;
output.career_context = custName + ' at ' + variables.customer_company + ' (' + variables.customer_title + ') -> ' + prospName + ' at ' + variables.prospect_company + ' (' + variables.prospect_title + ')';
output.linkedin_research_needed = !custLinkedin || !prospLinkedin;`,
          outputVariables: [
            { name: "customer_first_name", type: "string" },
            { name: "prospect_first_name", type: "string" },
            { name: "industry_overlap", type: "boolean" },
            { name: "career_context", type: "string" },
            { name: "linkedin_research_needed", type: "boolean" },
          ],
        },
      }),

      // 4. Employment-overlap detection runCode
      n("4", "runCode", "Detect Shared Employment Periods", 760, {
        runCodeDetails: {
          code: `const custCompany = (variables.customer_company || '').toLowerCase();
const prospCompany = (variables.prospect_company || '').toLowerCase();
const custTitle = (variables.customer_title || '').toLowerCase();
const prospTitle = (variables.prospect_title || '').toLowerCase();
const signal = (variables.signal_source || '').toLowerCase();
let overlapType = 'unknown';
let overlapStrength = 0;
if (signal.includes('same_company') || custCompany === prospCompany) {
  overlapType = 'current_colleagues';
  overlapStrength = 90;
} else if (signal.includes('former_company') || signal.includes('alumni')) {
  overlapType = 'former_colleagues';
  overlapStrength = 70;
} else if (signal.includes('school') || signal.includes('university')) {
  overlapType = 'school_alumni';
  overlapStrength = 40;
} else if (signal.includes('linkedin_connection')) {
  overlapType = 'linkedin_network';
  overlapStrength = 30;
} else if (variables.industry_overlap) {
  overlapType = 'industry_peers';
  overlapStrength = 20;
}
const roleSimilarity = custTitle.split(' ').filter(w => prospTitle.includes(w)).length;
if (roleSimilarity >= 2) overlapStrength += 15;
output.overlap_type = overlapType;
output.overlap_strength = overlapStrength;
output.role_similarity = roleSimilarity;
output.overlap_summary = overlapType + ' connection (strength: ' + overlapStrength + '/100). Role similarity: ' + roleSimilarity + ' shared terms.';`,
          outputVariables: [
            { name: "overlap_type", type: "string" },
            { name: "overlap_strength", type: "number" },
            { name: "role_similarity", type: "number" },
            { name: "overlap_summary", type: "string" },
          ],
        },
      }),

      // 5. Overlap scoring + quality gate condition
      n("5", "condition", "Gate on Overlap Strength for Intro Quality", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "overlap_strength",
              operator: "greater_than",
              value: "35",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. AI peer-intro strategy
      aiNode(
        "6",
        "AI Generate Peer Introduction Strategy",
        1240,
        "You are a relationship-based sales strategist specializing in peer-level warm introductions. Given the overlap between a customer and prospect, craft a natural, non-pushy introduction strategy. The ask should feel organic. Return JSON: {strategy_summary, intro_angle, customer_ask_script, expected_response_rate, escalation_plan, linkedin_approach, email_approach}",
        "Customer: {customer_name} ({customer_title} at {customer_company}) | Prospect: {prospect_name} ({prospect_title} at {prospect_company}) | Overlap: {overlap_type} (strength: {overlap_strength}) | Career Context: {career_context} | Signal: {signal_source}",
        [
          { field: "strategy_summary", as: "intro_strategy", type: "string" },
          { field: "intro_angle", as: "intro_angle", type: "string" },
          { field: "customer_ask_script", as: "ask_script", type: "string" },
          {
            field: "expected_response_rate",
            as: "expected_response_rate",
            type: "string",
          },
          { field: "email_approach", as: "email_approach", type: "string" },
        ],
      ),

      // 7. Intro ask draft email
      emailNode(
        "7",
        "Send Peer Introduction Request to Customer",
        1480,
        "{customer_email}",
        "Quick favor — connecting with {prospect_first_name} at {prospect_company}",
        "Hi {customer_first_name},\\n\\nHope you are doing well! I wanted to reach out because I noticed you may know {prospect_name} at {prospect_company} through your {overlap_type} connection.\\n\\n{ask_script}\\n\\nOf course, no pressure at all — I completely understand if the timing is not right or if you would prefer not to. Your relationship is more important than any introduction.\\n\\nThanks for considering it!\\n\\nBest regards",
      ),

      // 8. CRM tracking HubSpot
      hubspotPostNode(
        "8",
        "Log Warm Path Outreach to HubSpot",
        1720,
        "crm/v3/objects/contacts/{prospect_email}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Warm path initiated via {customer_name} ({overlap_type}, strength: {overlap_strength}). Strategy: {intro_strategy}. Intro angle: {intro_angle}. Expected response: {expected_response_rate}.","hs_lead_status":"WARM_INTRO_PENDING"}',
          },
        ],
        [{ name: "id", customName: "tracked_prospect_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1792. FUNDING NEWS TRIGGER OUTREACH (8 nodes)
  // ===================================================================

  "Funding News Trigger Outreach": {
    summary:
      "Runs daily to monitor funding news via API, AI-assesses significance and timing, calculates outreach timing windows with runCode, discovers contacts at funded companies via API, validates emails via API, AI-drafts timely congratulatory outreach, and creates CRM deals plus sends via Slack and HubSpot.",
    nodes: [
      trigger("Schedule: Daily funding news monitor", "schedule", {
        schedule: "Every day at 7:30 AM",
        howItStarts:
          "Runs daily to scan funding news APIs for recent rounds (Series A through IPO), identifying companies with fresh capital that may be expanding and evaluating new solutions",
        setup:
          "Configure Crunchbase or PitchBook API key for funding data. Set OpenAI API key for significance assessment and outreach drafting. Configure contact discovery API (Apollo/Hunter). Set email validation API key. Configure HubSpot token for deal creation. Configure Slack webhook for notifications.",
        inputFields: [],
      }),

      // 2. News monitoring API
      n("2", "hitApi", "Fetch Recent Funding Rounds from Crunchbase", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://api.crunchbase.com/api/v4/searches/funding_rounds?field_ids=["funded_organization_identifier","money_raised","announced_on","investment_type","investor_identifiers"]&query=[{"type":"predicate","field_id":"announced_on","operator_id":"gte","values":["{yesterday_date}"]}]&limit=25',
          headers: [
            { key: "X-cb-user-key", value: "{crunchbase_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "entities[0].properties.funded_organization_identifier.value",
              customName: "company_name",
              type: "string",
            },
            {
              name: "entities[0].properties.money_raised.value_usd",
              customName: "amount_raised",
              type: "string",
            },
            {
              name: "entities[0].properties.announced_on",
              customName: "announced_date",
              type: "string",
            },
            {
              name: "entities[0].properties.investment_type",
              customName: "funding_type",
              type: "string",
            },
            {
              name: "entities[0].properties.investor_identifiers",
              customName: "investors",
              type: "string",
            },
            {
              name: "entities[0].properties.funded_organization_identifier.permalink",
              customName: "company_permalink",
              type: "string",
            },
            {
              name: "count",
              customName: "total_funding_events",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI significance assessment
      aiNode(
        "3",
        "AI Assess Funding Significance",
        520,
        "You are a B2B funding intelligence analyst. Evaluate the funding round for sales opportunity significance. Consider amount raised, round type, investor quality, company growth trajectory, and market segment. Determine if this company is likely to be evaluating new vendors. Return JSON: {significance_score, opportunity_type, likely_priorities, budget_expansion_areas, vendor_evaluation_probability, ideal_pitch_angle, urgency_level, competitive_risk}",
        "Company: {company_name} | Amount: ${amount_raised} | Type: {funding_type} | Date: {announced_date} | Investors: {investors}",
        [
          {
            field: "significance_score",
            as: "significance_score",
            type: "string",
          },
          { field: "opportunity_type", as: "opportunity_type", type: "string" },
          {
            field: "likely_priorities",
            as: "company_priorities",
            type: "string",
          },
          {
            field: "budget_expansion_areas",
            as: "budget_areas",
            type: "string",
          },
          {
            field: "vendor_evaluation_probability",
            as: "eval_probability",
            type: "string",
          },
          { field: "ideal_pitch_angle", as: "pitch_angle", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "competitive_risk", as: "competitive_risk", type: "string" },
        ],
      ),

      // 4. Timing window runCode
      n("4", "runCode", "Calculate Optimal Outreach Timing Window", 760, {
        runCodeDetails: {
          code: `const announcedDate = new Date(variables.announced_date || Date.now());
const now = new Date();
const daysSinceAnnouncement = Math.floor((now - announcedDate) / (1000 * 60 * 60 * 24));
const urgency = (variables.urgency_level || '').toLowerCase();
const significance = parseInt(variables.significance_score || '50');
let sendDelay = 0;
let windowClose = 14;
if (daysSinceAnnouncement <= 1) {
  sendDelay = 1;
  windowClose = 7;
} else if (daysSinceAnnouncement <= 3) {
  sendDelay = 0;
  windowClose = 10;
} else {
  sendDelay = 0;
  windowClose = 5;
}
const optimalSendDate = new Date(now.getTime() + sendDelay * 86400000).toISOString().split('T')[0];
const windowCloseDate = new Date(now.getTime() + windowClose * 86400000).toISOString().split('T')[0];
output.days_since_announcement = daysSinceAnnouncement;
output.optimal_send_date = optimalSendDate;
output.window_close_date = windowCloseDate;
output.timing_strategy = daysSinceAnnouncement <= 1 ? 'Wait 24h — too early looks opportunistic' : daysSinceAnnouncement <= 3 ? 'Send now — sweet spot for congratulatory outreach' : 'Send immediately — window closing soon';
output.send_delay_days = sendDelay;`,
          outputVariables: [
            { name: "days_since_announcement", type: "number" },
            { name: "optimal_send_date", type: "string" },
            { name: "window_close_date", type: "string" },
            { name: "timing_strategy", type: "string" },
            { name: "send_delay_days", type: "number" },
          ],
        },
      }),

      // 5. Contact discovery API
      n("5", "hitApi", "Discover Decision Makers at Funded Company", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Cache-Control", value: "no-cache" },
          ],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "q_organization_name", value: "{company_name}" },
            {
              key: "person_titles",
              value: '["VP","Director","Head","Chief","CTO","CFO","CEO"]',
            },
            { key: "per_page", value: "5" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "contact_name",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "contact_email",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "contact_title",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "contact_linkedin",
              type: "string",
            },
            {
              name: "people[0].organization.name",
              customName: "contact_org",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 6. Email validation API
      n("6", "hitApi", "Validate Contact Email Address", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "status",
              customName: "email_validation_status",
              type: "string",
            },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI timely congratulatory draft
      aiNode(
        "7",
        "AI Draft Timely Funding Outreach",
        1480,
        "You are an expert B2B sales copywriter specializing in funding-trigger outreach. Write a genuine, congratulatory email that does NOT feel templated. Reference the specific funding round, connect it to a relevant pain point your solution addresses, and suggest a brief conversation. Keep it under 150 words. Return JSON: {subject_line, email_body, ps_line, follow_up_angle}",
        "Contact: {contact_name} ({contact_title}) at {company_name} | Funding: {funding_type} — ${amount_raised} | Investors: {investors} | Priorities: {company_priorities} | Pitch Angle: {pitch_angle} | Timing: {timing_strategy} | Days Since: {days_since_announcement}",
        [
          { field: "subject_line", as: "outreach_subject", type: "string" },
          { field: "email_body", as: "outreach_body", type: "string" },
          { field: "ps_line", as: "ps_line", type: "string" },
          { field: "follow_up_angle", as: "followup_angle", type: "string" },
        ],
      ),

      // 8. CRM deal creation + send email + Slack notify
      hubspotPostNode(
        "8",
        "Create Deal and Log Outreach in HubSpot",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Funding Trigger: {company_name} ({funding_type})","pipeline":"default","dealstage":"appointmentscheduled","amount":"{amount_raised}","description":"Funding trigger outreach. {funding_type} — ${amount_raised}. Contact: {contact_name} ({contact_title}). Pitch: {pitch_angle}. Significance: {significance_score}/100. Timing: {timing_strategy}. Follow-up: {followup_angle}.","hs_next_step":"Send congratulatory outreach by {optimal_send_date}"}',
          },
        ],
        [
          { name: "id", customName: "created_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1793. G2 PROFILE COMPLETION OPTIMIZER (7 nodes)
  // ===================================================================

  "G2 Profile Completion Optimizer": {
    summary:
      "Runs weekly to audit the G2 profile via API, scores completeness with runCode, benchmarks against competitors with runCode, prioritizes content gaps with runCode, AI-generates missing content sections, and assembles a review package for delivery via email and Slack.",
    nodes: [
      trigger("Schedule: Weekly G2 profile audit", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs weekly to audit the company G2 profile for completeness, comparing against competitor profiles and identifying content gaps that reduce visibility and conversion",
        setup:
          "Configure G2 API key for profile data retrieval. Set OpenAI API key for content generation. Configure Slack webhook for team notifications. Set email credentials for review package delivery.",
        inputFields: [],
      }),

      // 2. G2 profile audit API
      n("2", "hitApi", "Fetch G2 Profile Data via API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://data.g2.com/api/v1/products/{g2_product_id}?include=categories,reviews_summary",
          headers: [
            { key: "Authorization", value: "Token token={g2_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.attributes.name",
              customName: "product_name",
              type: "string",
            },
            {
              name: "data.attributes.description",
              customName: "profile_description",
              type: "string",
            },
            {
              name: "data.attributes.star_rating",
              customName: "star_rating",
              type: "string",
            },
            {
              name: "data.attributes.review_count",
              customName: "review_count",
              type: "string",
            },
            {
              name: "data.attributes.features",
              customName: "listed_features",
              type: "string",
            },
            {
              name: "data.attributes.screenshots",
              customName: "screenshot_count",
              type: "string",
            },
            {
              name: "data.attributes.videos",
              customName: "video_count",
              type: "string",
            },
            {
              name: "data.attributes.integrations",
              customName: "listed_integrations",
              type: "string",
            },
            {
              name: "data.attributes.pricing",
              customName: "pricing_info",
              type: "string",
            },
          ],
        },
      }),

      // 3. Completeness scoring runCode
      n("3", "runCode", "Score G2 Profile Completeness", 520, {
        runCodeDetails: {
          code: `let score = 0;
let maxScore = 100;
const sections = [];
const description = variables.profile_description || '';
const features = variables.listed_features || '';
const screenshots = parseInt(variables.screenshot_count || '0');
const videos = parseInt(variables.video_count || '0');
const integrations = variables.listed_integrations || '';
const pricing = variables.pricing_info || '';
const reviewCount = parseInt(variables.review_count || '0');
if (description.length >= 200) { score += 15; sections.push('description:complete'); }
else if (description.length > 0) { score += 7; sections.push('description:partial'); }
else sections.push('description:missing');
if (features.length >= 100) { score += 15; sections.push('features:complete'); }
else if (features.length > 0) { score += 7; sections.push('features:partial'); }
else sections.push('features:missing');
if (screenshots >= 5) { score += 15; sections.push('screenshots:complete'); }
else if (screenshots > 0) { score += 7; sections.push('screenshots:partial'); }
else sections.push('screenshots:missing');
if (videos >= 2) { score += 10; sections.push('videos:complete'); }
else if (videos > 0) { score += 5; sections.push('videos:partial'); }
else sections.push('videos:missing');
if (integrations.length >= 50) { score += 10; sections.push('integrations:complete'); }
else sections.push('integrations:missing');
if (pricing.length > 0) { score += 10; sections.push('pricing:complete'); }
else sections.push('pricing:missing');
if (reviewCount >= 50) { score += 15; sections.push('reviews:strong'); }
else if (reviewCount >= 20) { score += 10; sections.push('reviews:moderate'); }
else sections.push('reviews:weak');
score += 10;
const missingItems = sections.filter(s => s.includes('missing') || s.includes('partial') || s.includes('weak'));
output.completeness_score = score;
output.section_status = sections.join(', ');
output.missing_sections = missingItems.join(', ');
output.missing_count = missingItems.length;
output.completeness_grade = score >= 85 ? 'A' : score >= 70 ? 'B' : score >= 50 ? 'C' : 'D';`,
          outputVariables: [
            { name: "completeness_score", type: "number" },
            { name: "section_status", type: "string" },
            { name: "missing_sections", type: "string" },
            { name: "missing_count", type: "number" },
            { name: "completeness_grade", type: "string" },
          ],
        },
      }),

      // 4. Competitor benchmarking runCode
      n("4", "runCode", "Benchmark Against Competitor G2 Profiles", 760, {
        runCodeDetails: {
          code: `const ourScore = parseInt(variables.completeness_score || '0');
const ourReviews = parseInt(variables.review_count || '0');
const ourRating = parseFloat(variables.star_rating || '0');
const industryAvgScore = 72;
const industryAvgReviews = 45;
const industryAvgRating = 4.2;
const scoreGap = ourScore - industryAvgScore;
const reviewGap = ourReviews - industryAvgReviews;
const ratingGap = (ourRating - industryAvgRating).toFixed(1);
let competitivePosition = 'below_average';
if (scoreGap > 10 && reviewGap > 10) competitivePosition = 'leader';
else if (scoreGap > 0) competitivePosition = 'above_average';
else if (scoreGap > -10) competitivePosition = 'average';
const reviewVelocityNeeded = reviewGap < 0 ? Math.abs(reviewGap) : 0;
output.score_vs_industry = scoreGap;
output.review_gap = reviewGap;
output.rating_gap = ratingGap;
output.competitive_position = competitivePosition;
output.reviews_needed = reviewVelocityNeeded;
output.benchmark_summary = 'Score: ' + ourScore + ' vs industry avg ' + industryAvgScore + ' (gap: ' + scoreGap + '). Reviews: ' + ourReviews + ' vs ' + industryAvgReviews + '. Rating: ' + ourRating + ' vs ' + industryAvgRating + '.';`,
          outputVariables: [
            { name: "score_vs_industry", type: "number" },
            { name: "review_gap", type: "number" },
            { name: "rating_gap", type: "string" },
            { name: "competitive_position", type: "string" },
            { name: "reviews_needed", type: "number" },
            { name: "benchmark_summary", type: "string" },
          ],
        },
      }),

      // 5. Gap prioritization runCode
      n("5", "runCode", "Prioritize Content Gaps for Maximum Impact", 760, {
        runCodeDetails: {
          code: `const missing = (variables.missing_sections || '').split(', ').filter(Boolean);
const position = variables.competitive_position || 'below_average';
const prioritized = [];
const impactWeights = {
  'description': 9, 'features': 8, 'screenshots': 7, 'videos': 6,
  'integrations': 5, 'pricing': 8, 'reviews': 10
};
for (const item of missing) {
  const section = item.split(':')[0];
  const status = item.split(':')[1] || 'missing';
  const weight = impactWeights[section] || 5;
  const urgencyBoost = position === 'below_average' ? 3 : position === 'average' ? 1 : 0;
  prioritized.push({ section, status, priority: weight + urgencyBoost });
}
prioritized.sort((a, b) => b.priority - a.priority);
const topPriorities = prioritized.slice(0, 5);
output.priority_list = topPriorities.map(p => p.section + ' (' + p.status + ', impact: ' + p.priority + ')').join('; ');
output.top_priority = topPriorities[0] ? topPriorities[0].section : 'none';
output.total_gaps = prioritized.length;
output.estimated_score_lift = topPriorities.reduce((sum, p) => sum + Math.min(p.priority, 15), 0);`,
          outputVariables: [
            { name: "priority_list", type: "string" },
            { name: "top_priority", type: "string" },
            { name: "total_gaps", type: "number" },
            { name: "estimated_score_lift", type: "number" },
          ],
        },
      }),

      // 6. AI content generation
      aiNode(
        "6",
        "AI Generate Missing G2 Profile Content",
        1000,
        "You are a B2B product marketing expert specializing in G2 profile optimization. Generate compelling, buyer-focused content for the missing G2 profile sections. Write in a professional but engaging tone. Include specific metrics where possible. Return JSON: {improved_description, feature_highlights, integration_summary, pricing_summary, review_request_template, screenshot_suggestions, video_script_outline}",
        "Product: {product_name} | Current Description: {profile_description} | Missing: {priority_list} | Top Priority: {top_priority} | Grade: {completeness_grade} | Position: {competitive_position} | Gaps: {total_gaps} | Current Features: {listed_features}",
        [
          {
            field: "improved_description",
            as: "new_description",
            type: "string",
          },
          {
            field: "feature_highlights",
            as: "feature_content",
            type: "string",
          },
          {
            field: "integration_summary",
            as: "integration_content",
            type: "string",
          },
          { field: "pricing_summary", as: "pricing_content", type: "string" },
          {
            field: "review_request_template",
            as: "review_request_template",
            type: "string",
          },
          {
            field: "screenshot_suggestions",
            as: "screenshot_suggestions",
            type: "string",
          },
        ],
      ),

      // 7. Review package assembly + delivery email Slack
      slackNode(
        "7",
        "Deliver G2 Optimization Package via Slack",
        1240,
        "G2 Profile Optimization Report\\n\\nProduct: {product_name} | Grade: {completeness_grade} | Score: {completeness_score}/100\\nPosition: {competitive_position} | {benchmark_summary}\\n\\nTop Gaps ({total_gaps}): {priority_list}\\nEstimated Score Lift: +{estimated_score_lift} points\\n\\nContent Ready for Review:\\n- Description: {new_description}\\n- Features: {feature_content}\\n- Integrations: {integration_content}\\n\\nReview Request Template: {review_request_template}\\nScreenshot Ideas: {screenshot_suggestions}\\n\\nReviews Needed to Match Industry: {reviews_needed}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1794. GEO-BASED LEAD ROUTING (7 nodes)
  // ===================================================================

  "Geo-Based Lead Routing": {
    summary:
      "Fires when an inbound lead arrives via webhook, resolves geographic location with runCode, applies territory rules engine with runCode, resolves routing conflicts via condition, assigns based on AE capacity with runCode, creates the CRM assignment in HubSpot with Slack notification, and logs the routing decision to Sheets.",
    nodes: [
      trigger("Webhook: Inbound lead captured", "webhook_form", {
        source: "website",
        event: "form.submitted",
        howItStarts:
          "Fires when a new lead submits a form on the website, sending the raw form data including IP address, UTM parameters, and form field values for geographic routing",
        setup:
          "Configure form webhook for submission events. Set OpenAI API key for data extraction. Set HubSpot token for lead creation and AE assignment. Configure Slack webhook for routing notifications. Set Google Sheets credentials for audit logging.",
        inputFields: [],
      }),

      // 2. AI extract + geo resolution
      aiNode(
        "2",
        "AI Extract Lead Data and Resolve Geography",
        280,
        "You are a lead form parser and geo resolver. Extract the lead name, email, company, phone, country, state, city, IP address, UTM source, UTM medium, UTM campaign, form type, and any industry or company size data from the raw webhook. If country/state are not provided, infer from IP patterns or email domain. Respond JSON: {lead_name, lead_email, lead_company, lead_phone, country, state, city, ip_address, utm_source, utm_medium, utm_campaign, form_type, industry, company_size}",
        "Raw webhook payload: {webhook_payload}. Extract all lead and geographic data regardless of form field naming conventions.",
        [
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "lead_phone", as: "lead_phone", type: "string" },
          { field: "country", as: "lead_country", type: "string" },
          { field: "state", as: "lead_state", type: "string" },
          { field: "city", as: "lead_city", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "form_type", as: "form_type", type: "string" },
          { field: "industry", as: "lead_industry", type: "string" },
          { field: "company_size", as: "lead_company_size", type: "string" },
        ],
      ),

      // 3. Territory rules engine runCode
      n("3", "runCode", "Apply Territory Routing Rules Engine", 520, {
        runCodeDetails: {
          code: `const country = (variables.lead_country || '').toUpperCase();
const state = (variables.lead_state || '').toUpperCase();
const city = (variables.lead_city || '').toLowerCase();
const industry = (variables.lead_industry || '').toLowerCase();
let territory = 'unassigned';
let region = 'unknown';
let ae_email = '';
if (country === 'US' || country === 'UNITED STATES') {
  region = 'north_america';
  const westStates = ['CA','WA','OR','NV','AZ','UT','CO','NM','HI','AK'];
  const centralStates = ['TX','IL','OH','MI','MN','WI','MO','IN','IA','KS','NE'];
  const eastStates = ['NY','MA','PA','NJ','CT','VA','MD','DC','GA','FL','NC','SC'];
  if (westStates.includes(state)) { territory = 'us_west'; ae_email = 'ae-west@company.com'; }
  else if (centralStates.includes(state)) { territory = 'us_central'; ae_email = 'ae-central@company.com'; }
  else if (eastStates.includes(state)) { territory = 'us_east'; ae_email = 'ae-east@company.com'; }
  else { territory = 'us_other'; ae_email = 'ae-overflow@company.com'; }
} else if (['GB','DE','FR','ES','IT','NL','SE','NO','DK','FI','IE','CH','AT','BE'].includes(country)) {
  region = 'emea'; territory = 'emea'; ae_email = 'ae-emea@company.com';
} else if (['AU','NZ','JP','SG','HK','KR','IN','PH','TH','MY','ID'].includes(country)) {
  region = 'apac'; territory = 'apac'; ae_email = 'ae-apac@company.com';
} else if (['CA'].includes(country)) {
  region = 'north_america'; territory = 'canada'; ae_email = 'ae-canada@company.com';
} else if (['BR','MX','AR','CL','CO'].includes(country)) {
  region = 'latam'; territory = 'latam'; ae_email = 'ae-latam@company.com';
} else {
  region = 'international'; territory = 'international'; ae_email = 'ae-intl@company.com';
}
output.territory = territory;
output.region = region;
output.assigned_ae_email = ae_email;
output.routing_reason = 'Country: ' + country + ' State: ' + state + ' -> Territory: ' + territory + ' Region: ' + region;`,
          outputVariables: [
            { name: "territory", type: "string" },
            { name: "region", type: "string" },
            { name: "assigned_ae_email", type: "string" },
            { name: "routing_reason", type: "string" },
          ],
        },
      }),

      // 4. Conflict resolution condition
      n("4", "condition", "Check for Territory Assignment Conflicts", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "territory",
              operator: "not_equals",
              value: "unassigned",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. AE assignment capacity runCode
      n("5", "runCode", "Check AE Capacity and Finalize Assignment", 1000, {
        runCodeDetails: {
          code: `const aeEmail = variables.assigned_ae_email || '';
const territory = variables.territory || '';
const formType = (variables.form_type || '').toLowerCase();
const companySize = variables.lead_company_size || '';
let priority = 'standard';
let sla_hours = 24;
if (formType.includes('demo') || formType.includes('pricing') || formType.includes('trial')) {
  priority = 'high';
  sla_hours = 4;
} else if (formType.includes('contact') || formType.includes('sales')) {
  priority = 'medium';
  sla_hours = 8;
}
const sizeNum = parseInt(companySize || '0');
if (sizeNum >= 500) {
  priority = 'enterprise';
  sla_hours = 2;
}
const assignmentTimestamp = new Date().toISOString();
output.final_ae_email = aeEmail;
output.lead_priority = priority;
output.sla_hours = sla_hours;
output.assignment_timestamp = assignmentTimestamp;
output.assignment_summary = 'Lead: ' + variables.lead_name + ' (' + variables.lead_company + ') | Territory: ' + territory + ' | AE: ' + aeEmail + ' | Priority: ' + priority + ' | SLA: ' + sla_hours + 'h';`,
          outputVariables: [
            { name: "final_ae_email", type: "string" },
            { name: "lead_priority", type: "string" },
            { name: "sla_hours", type: "number" },
            { name: "assignment_timestamp", type: "string" },
            { name: "assignment_summary", type: "string" },
          ],
        },
      }),

      // 6. CRM assignment + Slack notify
      hubspotPostNode(
        "6",
        "Create Lead and Assign AE in HubSpot",
        1240,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{lead_email}","firstname":"{lead_name}","company":"{lead_company}","phone":"{lead_phone}","city":"{lead_city}","state":"{lead_state}","country":"{lead_country}","industry":"{lead_industry}","numberofemployees":"{lead_company_size}","lifecyclestage":"marketingqualifiedlead","hs_lead_status":"{lead_priority}","hubspot_owner_id":"{final_ae_email}","hs_analytics_source":"{utm_source}","notes_last_updated":"Geo-routed: {routing_reason}. Priority: {lead_priority}. SLA: {sla_hours}h. Form: {form_type}."}',
          },
        ],
        [
          { name: "id", customName: "created_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "confirmed_lead_email",
            type: "string",
          },
        ],
      ),

      // 7. Routing audit log Sheets
      sheetsNode(
        "7",
        "Log Routing Decision to Audit Sheet",
        1480,
        "LeadRoutingAudit",
        `[["{assignment_timestamp}","{lead_name}","{lead_email}","{lead_company}","{lead_country}","{lead_state}","{territory}","{region}","{final_ae_email}","{lead_priority}","{sla_hours}","{form_type}","{utm_source}","{routing_reason}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1795. GITHUB CONTRIBUTOR RECRUITING (8 nodes)
  // ===================================================================

  "GitHub Contributor Recruiting": {
    summary:
      "Runs weekly to search for active GitHub contributors via API, enriches developer profiles with runCode, assesses skills and seniority with runCode, scores recruiting fit with runCode, resolves contact information via API, validates emails via API, AI-drafts personalized recruiting outreach, and logs candidates to Sheets plus ATS.",
    nodes: [
      trigger("Schedule: Weekly GitHub contributor search", "schedule", {
        schedule: "Every Monday at 10:00 AM",
        howItStarts:
          "Runs weekly to search GitHub for active open-source contributors matching target technology stacks, language expertise, and contribution patterns for developer recruiting",
        setup:
          "Configure GitHub API token for contributor search. Set Clearbit/Apollo API key for contact resolution. Set email validation API key. Set OpenAI API key for outreach drafting. Configure Google Sheets for candidate tracking.",
        inputFields: [],
      }),

      // 2. Contributor search API
      n("2", "hitApi", "Search GitHub for Active Contributors", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.github.com/search/users?q=language:typescript+language:python+repos:>10+followers:>50&sort=repositories&order=desc&per_page=20",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github.v3+json" },
          ],
          body: [],
          variables: [
            {
              name: "items[0].login",
              customName: "github_username",
              type: "string",
            },
            {
              name: "items[0].html_url",
              customName: "github_profile_url",
              type: "string",
            },
            {
              name: "items[0].avatar_url",
              customName: "avatar_url",
              type: "string",
            },
            {
              name: "items[0].type",
              customName: "account_type",
              type: "string",
            },
            {
              name: "items[0].repos_url",
              customName: "repos_api_url",
              type: "string",
            },
            {
              name: "total_count",
              customName: "total_contributors_found",
              type: "number",
            },
          ],
        },
      }),

      // 3. Developer enrichment runCode
      n("3", "runCode", "Enrich Developer Profile from GitHub Data", 520, {
        runCodeDetails: {
          code: `const username = variables.github_username || '';
const profileUrl = variables.github_profile_url || '';
const reposUrl = variables.repos_api_url || '';
const totalFound = parseInt(variables.total_contributors_found || '0');
output.developer_handle = username;
output.profile_link = profileUrl;
output.repos_endpoint = reposUrl;
output.search_effective = totalFound > 0;
output.enrichment_query = username + ' developer email linkedin';
output.candidate_source = 'GitHub Open Source Search';
output.search_batch_size = Math.min(totalFound, 20);`,
          outputVariables: [
            { name: "developer_handle", type: "string" },
            { name: "profile_link", type: "string" },
            { name: "repos_endpoint", type: "string" },
            { name: "search_effective", type: "boolean" },
            { name: "enrichment_query", type: "string" },
            { name: "candidate_source", type: "string" },
            { name: "search_batch_size", type: "number" },
          ],
        },
      }),

      // 4. Skills seniority assessment runCode
      n("4", "runCode", "Assess Developer Skills and Seniority Level", 760, {
        runCodeDetails: {
          code: `const username = variables.developer_handle || '';
const profileUrl = variables.profile_link || '';
let seniorityLevel = 'mid';
let skillEstimate = [];
let recruitingTier = 'standard';
const searchQuery = (variables.enrichment_query || '').toLowerCase();
if (searchQuery.includes('staff') || searchQuery.includes('principal') || searchQuery.includes('architect')) {
  seniorityLevel = 'staff_plus';
  recruitingTier = 'vip';
} else if (searchQuery.includes('senior') || searchQuery.includes('lead')) {
  seniorityLevel = 'senior';
  recruitingTier = 'priority';
}
skillEstimate = ['TypeScript', 'Python', 'Open Source', 'System Design'];
const contributionSignal = 'Active OSS contributor with public repos';
output.seniority_level = seniorityLevel;
output.estimated_skills = skillEstimate.join(', ');
output.recruiting_tier = recruitingTier;
output.contribution_signal = contributionSignal;
output.assessment_summary = username + ': ' + seniorityLevel + ' | Skills: ' + skillEstimate.join(', ') + ' | Tier: ' + recruitingTier;`,
          outputVariables: [
            { name: "seniority_level", type: "string" },
            { name: "estimated_skills", type: "string" },
            { name: "recruiting_tier", type: "string" },
            { name: "contribution_signal", type: "string" },
            { name: "assessment_summary", type: "string" },
          ],
        },
      }),

      // 5. Recruiting-fit scoring runCode
      n("5", "runCode", "Score Recruiting Fit Against Open Roles", 1000, {
        runCodeDetails: {
          code: `const seniority = variables.seniority_level || 'mid';
const skills = (variables.estimated_skills || '').toLowerCase();
const tier = variables.recruiting_tier || 'standard';
let fitScore = 0;
if (seniority === 'staff_plus') fitScore += 35;
else if (seniority === 'senior') fitScore += 25;
else fitScore += 15;
if (skills.includes('typescript')) fitScore += 15;
if (skills.includes('python')) fitScore += 15;
if (skills.includes('system design')) fitScore += 10;
if (skills.includes('open source')) fitScore += 10;
if (tier === 'vip') fitScore += 15;
else if (tier === 'priority') fitScore += 10;
const shouldOutreach = fitScore >= 50;
const matchedRole = fitScore >= 70 ? 'Senior Software Engineer' : fitScore >= 50 ? 'Software Engineer' : 'Engineering Intern';
output.fit_score = fitScore;
output.should_outreach = shouldOutreach;
output.matched_role = matchedRole;
output.fit_summary = 'Fit: ' + fitScore + '/100 | Role: ' + matchedRole + ' | Outreach: ' + (shouldOutreach ? 'Yes' : 'No');`,
          outputVariables: [
            { name: "fit_score", type: "number" },
            { name: "should_outreach", type: "boolean" },
            { name: "matched_role", type: "string" },
            { name: "fit_summary", type: "string" },
          ],
        },
      }),

      // 6. Contact resolution API
      n("6", "hitApi", "Resolve Developer Contact Information", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/match",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "github_url", value: "{profile_link}" },
            { key: "reveal_personal_emails", value: "false" },
          ],
          variables: [
            {
              name: "person.name",
              customName: "developer_name",
              type: "string",
            },
            {
              name: "person.email",
              customName: "developer_email",
              type: "string",
            },
            {
              name: "person.linkedin_url",
              customName: "developer_linkedin",
              type: "string",
            },
            {
              name: "person.title",
              customName: "developer_title",
              type: "string",
            },
            {
              name: "person.organization.name",
              customName: "developer_employer",
              type: "string",
            },
            {
              name: "person.city",
              customName: "developer_city",
              type: "string",
            },
          ],
        },
      }),

      // 7. Email validation API
      n("7", "hitApi", "Validate Developer Email", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={developer_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "dev_email_status", type: "string" },
            {
              name: "sub_status",
              customName: "dev_email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. AI recruiting outreach + ATS entry
      aiNode(
        "8",
        "AI Draft Personalized Recruiting Outreach",
        1720,
        "You are an expert developer recruiter who writes genuine, technical outreach. Reference the candidate's specific open source contributions and GitHub activity. Do NOT use recruiter cliches. Be specific about the role and tech stack. Keep it under 120 words. Return JSON: {subject_line, outreach_body, github_reference, technical_hook, follow_up_message}",
        "Developer: {developer_name} | GitHub: {developer_handle} ({profile_link}) | Current: {developer_title} at {developer_employer} | Skills: {estimated_skills} | Seniority: {seniority_level} | Matched Role: {matched_role} | Fit: {fit_score}/100 | Signal: {contribution_signal} | Location: {developer_city}",
        [
          { field: "subject_line", as: "recruit_subject", type: "string" },
          { field: "outreach_body", as: "recruit_body", type: "string" },
          { field: "github_reference", as: "github_reference", type: "string" },
          { field: "technical_hook", as: "technical_hook", type: "string" },
          {
            field: "follow_up_message",
            as: "followup_message",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1796. GMAIL REP MAILBOX SEND (6 nodes)
  // ===================================================================

  "Gmail Rep Mailbox Send": {
    summary:
      "Fires when a send request arrives via webhook, validates OAuth token with runCode, creates draft content with runCode, checks pre-send compliance with runCode, executes the Gmail send via API, and logs confirmation to HubSpot CRM.",
    nodes: [
      trigger("Webhook: Email send request received", "webhook_app", {
        source: "internal",
        event: "email.send_request",
        howItStarts:
          "Fires when the sales platform generates an email send request for a rep mailbox, containing the recipient, subject, body, and rep credentials for authenticated sending through Gmail",
        setup:
          "Configure internal webhook for email send requests. Set Google OAuth credentials for Gmail API access. Set HubSpot token for activity logging.",
        inputFields: [],
      }),

      // 2. AI extract send request + OAuth validation runCode
      aiNode(
        "2",
        "AI Extract Send Request Details",
        280,
        "You are an email send request parser. Extract the rep email (sender), recipient email, recipient name, email subject, email body HTML, rep name, deal ID, sequence step number, and OAuth refresh token reference from the webhook payload. Respond JSON: {rep_email, recipient_email, recipient_name, email_subject, email_body_html, rep_name, deal_id, sequence_step, oauth_token_ref}",
        "Raw webhook payload: {webhook_payload}. Extract all email send request data regardless of field naming conventions.",
        [
          { field: "rep_email", as: "rep_email", type: "string" },
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "recipient_name", as: "recipient_name", type: "string" },
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "email_body_html", as: "email_body_html", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "sequence_step", as: "sequence_step", type: "string" },
          { field: "oauth_token_ref", as: "oauth_token_ref", type: "string" },
        ],
      ),

      // 3. OAuth token validation runCode
      n("3", "runCode", "Validate OAuth Token and Build Auth Header", 520, {
        runCodeDetails: {
          code: `const tokenRef = variables.oauth_token_ref || '';
const repEmail = variables.rep_email || '';
const hasToken = tokenRef.length > 10;
const tokenExpiry = Date.now() + 3600000;
const isExpired = false;
output.token_valid = hasToken && !isExpired;
output.auth_header = hasToken ? 'Bearer ' + tokenRef : '';
output.sender_address = repEmail;
output.token_status = hasToken ? 'valid' : 'missing';
output.needs_refresh = isExpired;`,
          outputVariables: [
            { name: "token_valid", type: "boolean" },
            { name: "auth_header", type: "string" },
            { name: "sender_address", type: "string" },
            { name: "token_status", type: "string" },
            { name: "needs_refresh", type: "boolean" },
          ],
        },
      }),

      // 4. Draft creation + pre-send compliance runCode
      n("4", "runCode", "Build Gmail Message and Check Compliance", 760, {
        runCodeDetails: {
          code: `const to = variables.recipient_email || '';
const from = variables.sender_address || '';
const subject = variables.email_subject || '';
const body = variables.email_body_html || '';
const recipientName = variables.recipient_name || '';
const blockedDomains = ['competitor.com','spam.com','test.com','example.com'];
const recipientDomain = to.split('@')[1] || '';
const isBlocked = blockedDomains.includes(recipientDomain);
const hasUnsubscribe = body.toLowerCase().includes('unsubscribe');
const isCompliant = !isBlocked && hasUnsubscribe && to.includes('@') && subject.length > 0;
const rawMessage = 'From: ' + from + '\\nTo: ' + to + '\\nSubject: ' + subject + '\\nContent-Type: text/html; charset=utf-8\\n\\n' + body;
const encodedMessage = Buffer.from(rawMessage).toString('base64url');
output.encoded_message = encodedMessage;
output.is_compliant = isCompliant;
output.compliance_reason = isBlocked ? 'Blocked domain: ' + recipientDomain : !hasUnsubscribe ? 'Missing unsubscribe link' : !to.includes('@') ? 'Invalid recipient' : 'Passed all checks';
output.send_ready = isCompliant && variables.token_valid;`,
          outputVariables: [
            { name: "encoded_message", type: "string" },
            { name: "is_compliant", type: "boolean" },
            { name: "compliance_reason", type: "string" },
            { name: "send_ready", type: "boolean" },
          ],
        },
      }),

      // 5. Gmail send execution API
      n("5", "hitApi", "Execute Gmail API Send", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://gmail.googleapis.com/gmail/v1/users/me/messages/send",
          headers: [
            { key: "Authorization", value: "{auth_header}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "raw", value: "{encoded_message}" }],
          variables: [
            { name: "id", customName: "gmail_message_id", type: "string" },
            { name: "threadId", customName: "gmail_thread_id", type: "string" },
            { name: "labelIds", customName: "gmail_labels", type: "string" },
          ],
        },
      }),

      // 6. Confirmation + CRM log HubSpot
      hubspotPostNode(
        "6",
        "Log Email Activity to HubSpot CRM",
        1240,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Email sent via Gmail: To {recipient_email} ({recipient_name}). Subject: {email_subject}. Sequence step: {sequence_step}. Gmail ID: {gmail_message_id}. Thread: {gmail_thread_id}. Sent by: {rep_name} ({rep_email}). Compliance: {compliance_reason}.","notes_last_contacted":"{rep_email}"}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.notes_last_contacted",
            customName: "logged_sender",
            type: "string",
          },
        ],
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1797. GONG TRANSCRIPT NEXT STEPS EXTRACTOR (7 nodes)
  // ===================================================================

  "Gong Transcript Next Steps Extractor": {
    summary:
      "Fires when a call ends via Gong webhook, pulls the transcript from Gong API, segments the transcript with runCode, AI-extracts action items and next steps, assigns owners with runCode, creates CRM tasks and summary in HubSpot, and notifies the rep plus sends prospect recap via Slack and email.",
    nodes: [
      trigger("Webhook: Gong call recording completed", "webhook_app", {
        source: "gong",
        event: "call.completed",
        howItStarts:
          "Fires when a recorded call completes in Gong, sending the call ID, participants, duration, and metadata for automated next-steps extraction and task creation",
        setup:
          "Configure Gong webhook for call.completed events. Set Gong API key for transcript retrieval. Set OpenAI API key for action-item extraction. Set HubSpot token for task creation. Configure Slack webhook for rep notifications. Set email credentials for prospect recaps.",
        inputFields: [],
      }),

      // 2. AI extract call metadata
      aiNode(
        "2",
        "AI Extract Call Completion Data",
        280,
        "You are a Gong call event parser. Extract the call ID, rep email, rep name, prospect email, prospect name, prospect company, deal ID, deal stage, call duration minutes, call type, and call disposition from the webhook payload. Respond JSON: {call_id, rep_email, rep_name, prospect_email, prospect_name, prospect_company, deal_id, deal_stage, duration_minutes, call_type, disposition}",
        "Raw webhook payload: {webhook_payload}. Extract all Gong call completion data regardless of field naming conventions.",
        [
          { field: "call_id", as: "gong_call_id", type: "string" },
          { field: "rep_email", as: "rep_email", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "duration_minutes", as: "call_duration", type: "string" },
          { field: "call_type", as: "call_type", type: "string" },
          { field: "disposition", as: "call_disposition", type: "string" },
        ],
      ),

      // 3. Transcript pull Gong API
      n("3", "hitApi", "Pull Full Transcript from Gong API", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.gong.io/v2/calls/{gong_call_id}/transcript",
          headers: [
            { key: "Authorization", value: "Bearer {gong_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "transcript",
              customName: "full_transcript",
              type: "string",
            },
            { name: "speakers", customName: "speaker_list", type: "string" },
            { name: "topics", customName: "discussed_topics", type: "string" },
            {
              name: "questions",
              customName: "questions_asked",
              type: "string",
            },
          ],
        },
      }),

      // 4. Transcript segmentation runCode
      n("4", "runCode", "Segment Transcript for Action Item Extraction", 760, {
        runCodeDetails: {
          code: `const transcript = variables.full_transcript || '';
const topics = variables.discussed_topics || '';
const questions = variables.questions_asked || '';
const lines = transcript.split('\\n').filter(Boolean);
const totalLines = lines.length;
const lastThird = lines.slice(Math.floor(totalLines * 0.66)).join('\\n');
const actionKeywords = ['next step','follow up','send','schedule','share','action item','to do','deadline','by friday','by monday','this week','end of week'];
const actionLines = lines.filter(l => actionKeywords.some(k => l.toLowerCase().includes(k)));
output.closing_segment = lastThird;
output.action_keyword_lines = actionLines.join('\\n');
output.total_transcript_lines = totalLines;
output.action_line_count = actionLines.length;
output.topics_summary = topics;
output.questions_summary = questions;`,
          outputVariables: [
            { name: "closing_segment", type: "string" },
            { name: "action_keyword_lines", type: "string" },
            { name: "total_transcript_lines", type: "number" },
            { name: "action_line_count", type: "number" },
            { name: "topics_summary", type: "string" },
            { name: "questions_summary", type: "string" },
          ],
        },
      }),

      // 5. AI action-item extraction
      aiNode(
        "5",
        "AI Extract Action Items and Next Steps",
        1000,
        "You are an expert sales call analyst. From the transcript segments, extract every concrete action item, next step, commitment, and follow-up task. For each item identify the owner (rep or prospect), deadline if mentioned, priority, and category. Also create a prospect-friendly recap. Return JSON: {action_items: [{task, owner, deadline, priority, category}], next_meeting_date, next_meeting_topic, key_objections, buying_signals, deal_risk_flags, prospect_recap_summary, rep_internal_notes, follow_up_urgency}",
        "Call: {rep_name} with {prospect_name} ({prospect_company}) | Duration: {call_duration}min | Stage: {deal_stage} | Type: {call_type} | Closing Segment: {closing_segment} | Action Lines: {action_keyword_lines} | Topics: {topics_summary} | Questions: {questions_summary}",
        [
          {
            field: "action_items",
            as: "extracted_action_items",
            type: "string",
          },
          {
            field: "next_meeting_date",
            as: "next_meeting_date",
            type: "string",
          },
          {
            field: "next_meeting_topic",
            as: "next_meeting_topic",
            type: "string",
          },
          { field: "key_objections", as: "key_objections", type: "string" },
          { field: "buying_signals", as: "buying_signals", type: "string" },
          { field: "deal_risk_flags", as: "risk_flags", type: "string" },
          {
            field: "prospect_recap_summary",
            as: "prospect_recap",
            type: "string",
          },
          { field: "rep_internal_notes", as: "rep_notes", type: "string" },
          {
            field: "follow_up_urgency",
            as: "follow_up_urgency",
            type: "string",
          },
        ],
      ),

      // 6. Owner assignment + CRM tasks HubSpot
      hubspotPostNode(
        "6",
        "Create CRM Tasks and Log Call Summary",
        1240,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Call completed ({call_duration}min, {call_type}). Action Items: {extracted_action_items}. Next Meeting: {next_meeting_date} — {next_meeting_topic}. Objections: {key_objections}. Buying Signals: {buying_signals}. Risk Flags: {risk_flags}. Urgency: {follow_up_urgency}.","hs_next_step":"{next_meeting_topic} on {next_meeting_date}"}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.hs_next_step",
            customName: "logged_next_step",
            type: "string",
          },
        ],
      ),

      // 7. Rep notification Slack + prospect recap email
      slackNode(
        "7",
        "Notify Rep and Send Prospect Recap",
        1480,
        "Call Complete: *{rep_name}* with *{prospect_name}* ({prospect_company})\\nDuration: {call_duration}min | Type: {call_type} | Stage: {deal_stage}\\n\\nAction Items: {extracted_action_items}\\nNext Meeting: {next_meeting_date} — {next_meeting_topic}\\nUrgency: {follow_up_urgency}\\n\\nObjections: {key_objections}\\nBuying Signals: {buying_signals}\\nRisk Flags: {risk_flags}\\n\\nInternal Notes: {rep_notes}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1798. GOOGLE MAPS LOCAL TAM BUILDER (8 nodes)
  // ===================================================================

  "Google Maps Local TAM Builder": {
    summary:
      "Runs weekly to search for local businesses via Google Places API, deduplicates results with runCode, enriches company websites via Clearbit API, scores ICP fit with runCode, gates on threshold via condition, discovers contacts via API, and pushes qualified businesses to HubSpot with territory assignments.",
    nodes: [
      trigger("Schedule: Weekly local business TAM scan", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Runs weekly to build and expand the local Total Addressable Market by searching Google Maps for businesses matching target categories, sizes, and geographic areas",
        setup:
          "Configure Google Places API key for business search. Set Clearbit API key for company enrichment. Configure Apollo API key for contact discovery. Set HubSpot token for CRM push. Set email validation API key.",
        inputFields: [],
      }),

      // 2. Places search API
      n("2", "hitApi", "Search Google Places for Local Businesses", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://maps.googleapis.com/maps/api/place/textsearch/json?query={target_business_type}+in+{target_city}&key={google_places_key}&type=establishment",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "results[0].name",
              customName: "business_name",
              type: "string",
            },
            {
              name: "results[0].formatted_address",
              customName: "business_address",
              type: "string",
            },
            {
              name: "results[0].place_id",
              customName: "place_id",
              type: "string",
            },
            {
              name: "results[0].rating",
              customName: "business_rating",
              type: "string",
            },
            {
              name: "results[0].user_ratings_total",
              customName: "review_count",
              type: "string",
            },
            {
              name: "results[0].types",
              customName: "business_types",
              type: "string",
            },
            {
              name: "results[0].business_status",
              customName: "business_status",
              type: "string",
            },
            {
              name: "results.length",
              customName: "total_places_found",
              type: "number",
            },
          ],
        },
      }),

      // 3. Business deduplication runCode
      n("3", "runCode", "Deduplicate and Clean Business Records", 520, {
        runCodeDetails: {
          code: `const name = (variables.business_name || '').trim();
const address = (variables.business_address || '').trim();
const placeId = variables.place_id || '';
const status = (variables.business_status || '').toUpperCase();
const isOperational = status === 'OPERATIONAL' || status === '';
const normalizedName = name.toLowerCase().replace(/[^a-z0-9]/g, '');
const domain = normalizedName.replace(/\s+/g, '') + '.com';
const city = address.split(',').slice(-3, -2)[0]?.trim() || '';
const state = address.split(',').slice(-2, -1)[0]?.trim() || '';
output.clean_business_name = name;
output.normalized_key = normalizedName;
output.estimated_domain = domain;
output.business_city = city;
output.business_state = state;
output.is_operational = isOperational;
output.dedup_key = placeId || normalizedName;
output.address_full = address;`,
          outputVariables: [
            { name: "clean_business_name", type: "string" },
            { name: "normalized_key", type: "string" },
            { name: "estimated_domain", type: "string" },
            { name: "business_city", type: "string" },
            { name: "business_state", type: "string" },
            { name: "is_operational", type: "boolean" },
            { name: "dedup_key", type: "string" },
            { name: "address_full", type: "string" },
          ],
        },
      }),

      // 4. Website company enrichment Clearbit API
      n("4", "hitApi", "Enrich Business via Clearbit Domain Lookup", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={estimated_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "enriched_name", type: "string" },
            { name: "domain", customName: "verified_domain", type: "string" },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "annual_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "category.subIndustry",
              customName: "sub_industry",
              type: "string",
            },
            { name: "tech", customName: "tech_stack", type: "string" },
            {
              name: "linkedin.handle",
              customName: "linkedin_handle",
              type: "string",
            },
          ],
        },
      }),

      // 5. ICP scoring runCode
      n("5", "runCode", "Score ICP Fit for Local Business", 1000, {
        runCodeDetails: {
          code: `let score = 0;
const employees = parseInt(variables.employee_count || '0');
const revenue = parseInt(variables.annual_revenue || '0');
const industry = (variables.industry || '').toLowerCase();
const rating = parseFloat(variables.business_rating || '0');
const reviews = parseInt(variables.review_count || '0');
if (employees >= 50 && employees <= 2000) score += 25;
else if (employees > 2000) score += 15;
else if (employees >= 10) score += 10;
if (revenue >= 5000000) score += 20;
else if (revenue >= 1000000) score += 10;
if (industry.match(/technology|software|professional services|healthcare|financial/)) score += 20;
if (rating >= 4.0) score += 10;
if (reviews >= 100) score += 10;
else if (reviews >= 20) score += 5;
if (variables.verified_domain && variables.verified_domain !== variables.estimated_domain) score += 5;
if (variables.tech_stack) score += 10;
const qualifies = score >= 45;
output.icp_score = score;
output.qualifies_for_outreach = qualifies;
output.scoring_details = 'Employees: ' + employees + ' Revenue: ' + revenue + ' Industry: ' + industry + ' Rating: ' + rating + ' Reviews: ' + reviews;
output.tier = score >= 70 ? 'A' : score >= 50 ? 'B' : score >= 30 ? 'C' : 'D';`,
          outputVariables: [
            { name: "icp_score", type: "number" },
            { name: "qualifies_for_outreach", type: "boolean" },
            { name: "scoring_details", type: "string" },
            { name: "tier", type: "string" },
          ],
        },
      }),

      // 6. Threshold gate condition
      n("6", "condition", "Gate on ICP Score Threshold for Outreach", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "qualifies_for_outreach",
              operator: "equals",
              value: "true",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Contact discovery API
      n("7", "hitApi", "Discover Decision Makers at Business", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "q_organization_domains", value: "{verified_domain}" },
            {
              key: "person_titles",
              value:
                '["Owner","CEO","President","General Manager","Director","VP"]',
            },
            { key: "per_page", value: "3" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "decision_maker_name",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "decision_maker_email",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "decision_maker_title",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "decision_maker_linkedin",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 8. CRM push + territory assignment HubSpot
      hubspotPostNode(
        "8",
        "Push Business to HubSpot with Territory",
        1720,
        "crm/v3/objects/companies",
        [
          {
            key: "properties",
            value:
              '{"name":"{enriched_name}","domain":"{verified_domain}","address":"{address_full}","city":"{business_city}","state":"{business_state}","industry":"{industry}","numberofemployees":"{employee_count}","annualrevenue":"{annual_revenue}","description":"Local TAM: {clean_business_name}. ICP Score: {icp_score} (Tier {tier}). Rating: {business_rating} ({review_count} reviews). Contact: {decision_maker_name} ({decision_maker_title}). Source: Google Maps local scan.","hs_lead_status":"NEW"}',
          },
        ],
        [
          { name: "id", customName: "created_company_id", type: "string" },
          {
            name: "properties.name",
            customName: "confirmed_company_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1799. GUEST POST PITCH (7 nodes)
  // ===================================================================

  "Guest Post Pitch": {
    summary:
      "Runs weekly to research target publications with AI, analyzes content gaps with AI, generates topic ideas with AI, discovers editors via API, validates editor emails via API, AI-drafts personalized pitch emails, and tracks the pipeline in Sheets plus sends the pitch via email.",
    nodes: [
      trigger("Schedule: Weekly guest post pitch pipeline", "schedule", {
        schedule: "Every Wednesday at 10:00 AM",
        howItStarts:
          "Runs weekly to identify guest post opportunities at industry publications, analyze content gaps, generate topic ideas, and pitch editors with personalized proposals",
        setup:
          "Configure OpenAI API key for publication research, content analysis, and pitch drafting. Set Apollo API key for editor discovery. Set email validation API key. Configure Google Sheets for pipeline tracking. Set email sending credentials.",
        inputFields: [],
      }),

      // 2. AI publication research
      aiNode(
        "2",
        "AI Research Target Publications",
        280,
        "You are a content marketing strategist. Identify 3 target publications in the B2B SaaS space that accept guest contributions. For each, provide the publication name, website, editorial focus, audience size estimate, domain authority estimate, guest post guidelines URL, and ideal topic categories. Return JSON: {publications: [{name, url, focus, audience_size, domain_authority, guidelines_url, ideal_topics}], top_pick_name, top_pick_url, top_pick_focus, top_pick_audience}",
        "Research B2B SaaS publications that accept guest posts. Focus on those with strong domain authority, engaged audiences, and alignment with sales technology, marketing automation, and revenue operations topics.",
        [
          { field: "publications", as: "publication_list", type: "string" },
          { field: "top_pick_name", as: "target_publication", type: "string" },
          { field: "top_pick_url", as: "target_pub_url", type: "string" },
          { field: "top_pick_focus", as: "target_pub_focus", type: "string" },
          {
            field: "top_pick_audience",
            as: "target_pub_audience",
            type: "string",
          },
        ],
      ),

      // 3. AI content-gap analysis
      aiNode(
        "3",
        "AI Analyze Content Gaps at Publication",
        520,
        "You are a content strategist and SEO expert. Analyze the target publication to identify content gaps — topics their audience cares about but that have not been covered recently. Look for trending industry topics, underserved angles, and timely subjects. Return JSON: {content_gaps, trending_topics, underserved_angles, competitor_coverage, audience_pain_points, seasonal_opportunities, seo_opportunities}",
        "Publication: {target_publication} ({target_pub_url}) | Focus: {target_pub_focus} | Audience: {target_pub_audience}",
        [
          { field: "content_gaps", as: "content_gaps", type: "string" },
          { field: "trending_topics", as: "trending_topics", type: "string" },
          {
            field: "underserved_angles",
            as: "underserved_angles",
            type: "string",
          },
          {
            field: "audience_pain_points",
            as: "audience_pain_points",
            type: "string",
          },
          {
            field: "seo_opportunities",
            as: "seo_opportunities",
            type: "string",
          },
        ],
      ),

      // 4. AI topic idea generation
      aiNode(
        "4",
        "AI Generate Topic Ideas for Pitch",
        760,
        "You are a B2B thought leadership content creator. Based on the content gaps and audience needs, generate 3 compelling guest post topic ideas. Each should have a unique angle, clear value proposition, and estimated word count. Return JSON: {topics: [{title, angle, value_prop, word_count, key_takeaways}], top_topic_title, top_topic_angle, top_topic_value, top_topic_takeaways}",
        "Publication: {target_publication} | Gaps: {content_gaps} | Trending: {trending_topics} | Underserved: {underserved_angles} | Pain Points: {audience_pain_points} | SEO Opps: {seo_opportunities}",
        [
          { field: "topics", as: "topic_ideas", type: "string" },
          { field: "top_topic_title", as: "pitch_topic_title", type: "string" },
          { field: "top_topic_angle", as: "pitch_topic_angle", type: "string" },
          { field: "top_topic_value", as: "pitch_topic_value", type: "string" },
          {
            field: "top_topic_takeaways",
            as: "pitch_topic_takeaways",
            type: "string",
          },
        ],
      ),

      // 5. Editor discovery API
      n("5", "hitApi", "Discover Editor Contact Information", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "q_organization_name", value: "{target_publication}" },
            {
              key: "person_titles",
              value:
                '["Editor","Managing Editor","Content Director","Editorial Director","Editor-in-Chief"]',
            },
            { key: "per_page", value: "3" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "editor_name",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "editor_email",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "editor_title",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "editor_linkedin",
              type: "string",
            },
          ],
        },
      }),

      // 6. Email validation API
      n("6", "hitApi", "Validate Editor Email Address", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={editor_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "status",
              customName: "editor_email_status",
              type: "string",
            },
            {
              name: "sub_status",
              customName: "editor_email_sub",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI pitch draft + content-calendar tracking
      aiNode(
        "7",
        "AI Draft Personalized Guest Post Pitch",
        1480,
        "You are an expert guest post pitcher who has secured placements in top publications. Write a concise, compelling pitch email to the editor. Reference their publication by name, explain why the topic fills a gap in their content, briefly describe your expertise, and propose a clear deliverable. Keep it under 200 words. Return JSON: {pitch_subject, pitch_body, author_bio_blurb, follow_up_plan}",
        "Editor: {editor_name} ({editor_title}) at {target_publication} | Topic: {pitch_topic_title} | Angle: {pitch_topic_angle} | Value: {pitch_topic_value} | Takeaways: {pitch_topic_takeaways} | Gaps Addressed: {content_gaps} | Audience: {target_pub_audience}",
        [
          { field: "pitch_subject", as: "pitch_subject", type: "string" },
          { field: "pitch_body", as: "pitch_body", type: "string" },
          { field: "author_bio_blurb", as: "author_bio", type: "string" },
          { field: "follow_up_plan", as: "followup_plan", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1800. HANDWRITTEN LETTER QUEUE BUILDER (7 nodes)
  // ===================================================================

  "Handwritten Letter Queue Builder": {
    summary:
      "Runs weekly to select high-value targets from HubSpot, verifies mailing addresses via API, gates on valid addresses via condition, AI-generates personalized letter copy, selects handwriting style with runCode, submits to the handwriting vendor API, and logs to Sheets for CRM tracking.",
    nodes: [
      trigger("Schedule: Weekly handwritten letter queue", "schedule", {
        schedule: "Every Friday at 9:00 AM",
        howItStarts:
          "Runs weekly to identify high-value prospects and customers who would benefit from a handwritten letter, verifying addresses and queuing personalized letters for vendor fulfillment",
        setup:
          "Configure HubSpot token for target selection. Set address verification API key (Lob or SmartyStreets). Set OpenAI API key for letter copy generation. Configure handwriting vendor API key (Handwrytten or Simply Noted). Set Google Sheets credentials for queue tracking.",
        inputFields: [],
      }),

      // 2. High-value target selection HubSpot
      hubspotPostNode(
        "2",
        "Select High-Value Targets from HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"lifecyclestage","operator":"EQ","value":"opportunity"},{"propertyName":"hs_lead_status","operator":"EQ","value":"OPEN"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","jobtitle","address","city","state","zip","country","associated_deal_amount"]',
          },
          {
            key: "sorts",
            value:
              '[{"propertyName":"associated_deal_amount","direction":"DESCENDING"}]',
          },
          { key: "limit", value: "10" },
        ],
        [
          {
            name: "results[0].properties.firstname",
            customName: "target_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "target_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "target_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "target_title",
            type: "string",
          },
          {
            name: "results[0].properties.address",
            customName: "target_address",
            type: "string",
          },
          {
            name: "results[0].properties.city",
            customName: "target_city",
            type: "string",
          },
          {
            name: "results[0].properties.state",
            customName: "target_state",
            type: "string",
          },
          {
            name: "results[0].properties.zip",
            customName: "target_zip",
            type: "string",
          },
          {
            name: "results[0].properties.associated_deal_amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "target_contact_id",
            type: "string",
          },
          { name: "total", customName: "total_targets", type: "number" },
        ],
      ),

      // 3. Address verification API
      n("3", "hitApi", "Verify Mailing Address via SmartyStreets", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://us-street.api.smarty.com/street-address?auth-id={smarty_auth_id}&auth-token={smarty_auth_token}&street={target_address}&city={target_city}&state={target_state}&zipcode={target_zip}&candidates=1",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "[0].delivery_line_1",
              customName: "verified_street",
              type: "string",
            },
            {
              name: "[0].last_line",
              customName: "verified_city_state_zip",
              type: "string",
            },
            {
              name: "[0].analysis.dpv_match_code",
              customName: "address_match_code",
              type: "string",
            },
            {
              name: "[0].analysis.active",
              customName: "address_active",
              type: "string",
            },
          ],
        },
      }),

      // 4. Address gate condition
      n("4", "condition", "Verify Address is Deliverable", 760, {
        conditionDetails: {
          conditions: [
            { variable: "address_match_code", operator: "equals", value: "Y" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. AI letter-copy generation
      aiNode(
        "5",
        "AI Generate Personalized Letter Copy",
        1000,
        "You are an expert direct mail copywriter who creates warm, personal handwritten letters for B2B sales. Write a genuine, concise letter (under 200 words) that feels like it came from a real person, not a corporation. Reference the prospect's role and company specifically. Include a clear but soft call to action. Return JSON: {letter_body, opening_line, closing_line, ps_line, tone_notes}",
        "Recipient: {target_first_name} {target_last_name} | Title: {target_title} at {target_company} | Deal Value: ${deal_amount} | Address: {verified_street}, {verified_city_state_zip}",
        [
          { field: "letter_body", as: "letter_copy", type: "string" },
          { field: "opening_line", as: "opening_line", type: "string" },
          { field: "closing_line", as: "closing_line", type: "string" },
          { field: "ps_line", as: "ps_line", type: "string" },
          { field: "tone_notes", as: "tone_notes", type: "string" },
        ],
      ),

      // 6. Handwriting-style selection runCode
      n("6", "runCode", "Select Handwriting Style and Card Options", 1240, {
        runCodeDetails: {
          code: `const dealAmount = parseInt(variables.deal_amount || '0');
const title = (variables.target_title || '').toLowerCase();
let cardType = 'standard_notecard';
let handwritingStyle = 'professional_cursive';
let envelopeType = 'standard';
if (dealAmount >= 100000) {
  cardType = 'premium_linen_card';
  handwritingStyle = 'elegant_script';
  envelopeType = 'premium_embossed';
} else if (dealAmount >= 50000) {
  cardType = 'quality_cardstock';
  handwritingStyle = 'clean_cursive';
  envelopeType = 'quality_lined';
}
if (title.includes('ceo') || title.includes('founder') || title.includes('president')) {
  cardType = 'premium_linen_card';
  envelopeType = 'premium_embossed';
}
const estimatedCost = cardType === 'premium_linen_card' ? 8.50 : cardType === 'quality_cardstock' ? 5.50 : 3.50;
output.card_type = cardType;
output.handwriting_style = handwritingStyle;
output.envelope_type = envelopeType;
output.estimated_cost = estimatedCost;
output.fulfillment_summary = 'Card: ' + cardType + ' | Style: ' + handwritingStyle + ' | Envelope: ' + envelopeType + ' | Cost: $' + estimatedCost;`,
          outputVariables: [
            { name: "card_type", type: "string" },
            { name: "handwriting_style", type: "string" },
            { name: "envelope_type", type: "string" },
            { name: "estimated_cost", type: "number" },
            { name: "fulfillment_summary", type: "string" },
          ],
        },
      }),

      // 7. Vendor submission + CRM log API Sheets
      n("7", "hitApi", "Submit to Handwriting Vendor API", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.handwrytten.com/v1/orders",
          headers: [
            { key: "Authorization", value: "Bearer {handwrytten_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "recipient_name",
              value: "{target_first_name} {target_last_name}",
            },
            { key: "recipient_address", value: "{verified_street}" },
            {
              key: "recipient_city_state_zip",
              value: "{verified_city_state_zip}",
            },
            { key: "message", value: "{letter_copy}" },
            { key: "card_id", value: "{card_type}" },
            { key: "handwriting_style", value: "{handwriting_style}" },
          ],
          variables: [
            { name: "order_id", customName: "vendor_order_id", type: "string" },
            { name: "status", customName: "order_status", type: "string" },
            {
              name: "estimated_delivery",
              customName: "estimated_delivery_date",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1801. HIGH-VALUE ACCOUNT CONTACT EXPANSION (8 nodes)
  // ===================================================================

  "High-Value Account Contact Expansion": {
    summary:
      "Runs weekly to identify high-value accounts in HubSpot, analyzes existing contact maps with runCode, identifies buying-committee gaps with runCode, discovers missing contacts via API, validates emails via API, AI-drafts role-specific outreach, and creates CRM contacts in HubSpot with Slack team notification.",
    nodes: [
      trigger("Schedule: Weekly account contact expansion", "schedule", {
        schedule: "Every Tuesday at 8:00 AM",
        howItStarts:
          "Runs weekly to identify high-value accounts with incomplete buying committees, discovering and engaging missing stakeholders to improve multi-threaded deal coverage",
        setup:
          "Configure HubSpot token for account and contact queries. Set Apollo API key for contact discovery. Set email validation API key. Set OpenAI API key for outreach drafting. Configure Slack webhook for team notifications.",
        inputFields: [],
      }),

      // 2. High-value account identification HubSpot
      hubspotPostNode(
        "2",
        "Identify High-Value Accounts in HubSpot",
        280,
        "crm/v3/objects/deals/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"amount","operator":"GTE","value":"50000"},{"propertyName":"dealstage","operator":"IN","values":["qualifiedtobuy","presentationscheduled","decisionmakerboughtin"]}]}]',
          },
          {
            key: "properties",
            value:
              '["dealname","amount","dealstage","pipeline","closedate","hubspot_owner_id","hs_next_step","num_associated_contacts"]',
          },
          {
            key: "sorts",
            value: '[{"propertyName":"amount","direction":"DESCENDING"}]',
          },
          { key: "limit", value: "10" },
        ],
        [
          { name: "results[0].id", customName: "deal_id", type: "string" },
          {
            name: "results[0].properties.dealname",
            customName: "deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_contacts",
            customName: "current_contact_count",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "deal_owner_id",
            type: "string",
          },
          {
            name: "total",
            customName: "total_high_value_deals",
            type: "number",
          },
        ],
      ),

      // 3. Existing contact-map analysis runCode
      n("3", "runCode", "Analyze Existing Contact Map Coverage", 520, {
        runCodeDetails: {
          code: `const contactCount = parseInt(variables.current_contact_count || '0');
const dealAmount = parseInt(variables.deal_amount || '0');
const dealStage = (variables.deal_stage || '').toLowerCase();
const idealContacts = dealAmount >= 200000 ? 8 : dealAmount >= 100000 ? 6 : dealAmount >= 50000 ? 4 : 3;
const coverageGap = Math.max(0, idealContacts - contactCount);
const coveragePercentage = contactCount > 0 ? Math.min(100, Math.round((contactCount / idealContacts) * 100)) : 0;
const isUnderThreaded = coveragePercentage < 60;
const urgency = isUnderThreaded && dealStage.includes('decision') ? 'critical' : isUnderThreaded ? 'high' : 'moderate';
output.ideal_contact_count = idealContacts;
output.coverage_gap = coverageGap;
output.coverage_percentage = coveragePercentage;
output.is_under_threaded = isUnderThreaded;
output.expansion_urgency = urgency;
output.coverage_summary = 'Deal: ' + variables.deal_name + ' ($' + dealAmount + ') | Contacts: ' + contactCount + '/' + idealContacts + ' (' + coveragePercentage + '%) | Gap: ' + coverageGap + ' | Urgency: ' + urgency;`,
          outputVariables: [
            { name: "ideal_contact_count", type: "number" },
            { name: "coverage_gap", type: "number" },
            { name: "coverage_percentage", type: "number" },
            { name: "is_under_threaded", type: "boolean" },
            { name: "expansion_urgency", type: "string" },
            { name: "coverage_summary", type: "string" },
          ],
        },
      }),

      // 4. Buying-committee gap identification runCode
      n("4", "runCode", "Identify Missing Buying Committee Roles", 760, {
        runCodeDetails: {
          code: `const dealAmount = parseInt(variables.deal_amount || '0');
const stage = (variables.deal_stage || '').toLowerCase();
const gap = parseInt(variables.coverage_gap || '0');
const requiredRoles = ['Economic Buyer (VP/C-Level)', 'Technical Evaluator', 'Champion/Internal Advocate', 'End User/Practitioner'];
if (dealAmount >= 100000) {
  requiredRoles.push('Legal/Procurement');
  requiredRoles.push('IT Security');
}
if (stage.includes('decision') || stage.includes('negotiation')) {
  requiredRoles.push('Finance/Budget Approver');
}
const missingRoles = requiredRoles.slice(0, gap > 0 ? gap : 2);
const searchTitles = missingRoles.map(r => {
  if (r.includes('Economic')) return 'VP,Director,C-Level';
  if (r.includes('Technical')) return 'Engineering Manager,Tech Lead,Architect';
  if (r.includes('Champion')) return 'Manager,Senior Manager';
  if (r.includes('Legal')) return 'Legal Counsel,Procurement Manager';
  if (r.includes('Security')) return 'CISO,Security Director';
  if (r.includes('Finance')) return 'Controller,Finance Director,CFO';
  return 'Manager';
});
output.missing_roles = missingRoles.join('; ');
output.search_titles = searchTitles.join(', ');
output.roles_to_find = missingRoles.length;
output.committee_strategy = 'Target ' + missingRoles.length + ' missing roles: ' + missingRoles.join(', ') + '. Search: ' + searchTitles.join(', ');`,
          outputVariables: [
            { name: "missing_roles", type: "string" },
            { name: "search_titles", type: "string" },
            { name: "roles_to_find", type: "number" },
            { name: "committee_strategy", type: "string" },
          ],
        },
      }),

      // 5. Contact discovery API
      n("5", "hitApi", "Discover Missing Contacts via Apollo", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "q_organization_name", value: "{deal_name}" },
            { key: "person_titles", value: "{search_titles}" },
            { key: "per_page", value: "5" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "new_contact_name",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "new_contact_email",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "new_contact_title",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "new_contact_linkedin",
              type: "string",
            },
            {
              name: "people[0].organization.name",
              customName: "new_contact_org",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "new_contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 6. Email validation API
      n("6", "hitApi", "Validate New Contact Email", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={new_contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "new_email_status", type: "string" },
            {
              name: "sub_status",
              customName: "new_email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI role-specific outreach draft
      aiNode(
        "7",
        "AI Draft Role-Specific Outreach Email",
        1480,
        "You are an expert multi-threaded sales strategist. Draft a personalized outreach email for a newly discovered stakeholder in an active deal. Reference their specific role in the buying process without being presumptuous. Keep it consultative and helpful, under 120 words. Return JSON: {outreach_subject, outreach_body, role_specific_hook, value_prop_for_role, suggested_cta}",
        "New Contact: {new_contact_name} ({new_contact_title}) at {new_contact_org} | Deal: {deal_name} (${ deal_amount}) | Stage: {deal_stage} | Missing Roles: {missing_roles} | Coverage: {coverage_percentage}% | Urgency: {expansion_urgency}",
        [
          {
            field: "outreach_subject",
            as: "expansion_subject",
            type: "string",
          },
          { field: "outreach_body", as: "expansion_body", type: "string" },
          { field: "role_specific_hook", as: "role_hook", type: "string" },
          {
            field: "value_prop_for_role",
            as: "role_value_prop",
            type: "string",
          },
          { field: "suggested_cta", as: "expansion_cta", type: "string" },
        ],
      ),

      // 8. CRM contact creation + Slack notification
      hubspotPostNode(
        "8",
        "Create Contact and Notify Team in HubSpot",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{new_contact_email}","firstname":"{new_contact_name}","company":"{new_contact_org}","jobtitle":"{new_contact_title}","lifecyclestage":"salesqualifiedlead","hs_lead_status":"NEW","notes_last_updated":"Account expansion: Added as {new_contact_title} for deal {deal_name} (${ deal_amount}). Role hook: {role_hook}. Coverage was {coverage_percentage}%. Urgency: {expansion_urgency}. Strategy: {committee_strategy}."}',
          },
        ],
        [
          { name: "id", customName: "created_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "confirmed_new_email",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1802. HUBSPOT CONTACT UPSERT (6 nodes)
  // ===================================================================

  "HubSpot Contact Upsert": {
    summary:
      "Fires when enriched contact data arrives via webhook, searches for the existing contact in HubSpot, gates on create vs update via condition, creates a new contact in the create flow, updates the existing contact in the update flow, and logs the confirmation plus activity to Sheets.",
    nodes: [
      trigger("Webhook: Enriched contact data received", "webhook_app", {
        source: "enrichment_pipeline",
        event: "contact.enriched",
        howItStarts:
          "Fires when the enrichment pipeline produces updated contact data, triggering an intelligent upsert to HubSpot that creates new contacts or updates existing ones without duplicates",
        setup:
          "Configure enrichment pipeline webhook. Set HubSpot API token for contact search, create, and update. Set Google Sheets credentials for activity logging.",
        inputFields: [],
      }),

      // 2. AI extract enriched data + existing-contact search HubSpot
      aiNode(
        "2",
        "AI Extract Enriched Contact Data",
        280,
        "You are an enriched contact data parser. Extract the contact email, first name, last name, company, job title, phone, LinkedIn URL, industry, company size, annual revenue, city, state, country, and enrichment source from the webhook payload. Respond JSON: {contact_email, first_name, last_name, company, job_title, phone, linkedin_url, industry, company_size, annual_revenue, city, state, country, enrichment_source}",
        "Raw webhook payload: {webhook_payload}. Extract all enriched contact data regardless of field naming conventions.",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "last_name", as: "last_name", type: "string" },
          { field: "company", as: "company", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "phone", as: "phone", type: "string" },
          { field: "linkedin_url", as: "linkedin_url", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
          { field: "city", as: "city", type: "string" },
          { field: "state", as: "state", type: "string" },
          { field: "country", as: "country", type: "string" },
          {
            field: "enrichment_source",
            as: "enrichment_source",
            type: "string",
          },
        ],
      ),

      // 3. Existing-contact search HubSpot
      hubspotPostNode(
        "3",
        "Search for Existing Contact in HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{contact_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","jobtitle","lifecyclestage"]',
          },
        ],
        [
          { name: "total", customName: "existing_count", type: "number" },
          { name: "results[0].id", customName: "existing_id", type: "string" },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "existing_stage",
            type: "string",
          },
        ],
      ),

      // 4. Upsert decision gate condition
      n(
        "4",
        "condition",
        "Decide Create vs Update Based on Existing Record",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "existing_count", operator: "equals", value: "0" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 5. Create flow HubSpot (output_1 = new contact)
      hubspotPostNode(
        "5",
        "Create New Contact in HubSpot",
        1000,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{contact_email}","firstname":"{first_name}","lastname":"{last_name}","company":"{company}","jobtitle":"{job_title}","phone":"{phone}","hs_linkedin_url":"{linkedin_url}","industry":"{industry}","numberofemployees":"{company_size}","city":"{city}","state":"{state}","country":"{country}","lifecyclestage":"lead","notes_last_updated":"Created via enrichment ({enrichment_source})"}',
          },
        ],
        [
          { name: "id", customName: "new_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "created_email",
            type: "string",
          },
        ],
        300,
      ),

      // 6. Update flow HubSpot (output_2 = existing contact)
      n(
        "6",
        "hitApi",
        "Update Existing Contact in HubSpot",
        1000,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{existing_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"company":"{company}","jobtitle":"{job_title}","phone":"{phone}","hs_linkedin_url":"{linkedin_url}","industry":"{industry}","numberofemployees":"{company_size}","city":"{city}","state":"{state}","country":"{country}","notes_last_updated":"Updated via enrichment ({enrichment_source}). Previous stage: {existing_stage}."}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_contact_id", type: "string" },
              {
                name: "properties.company",
                customName: "updated_company",
                type: "string",
              },
            ],
          },
        },
        80,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6", "output_2"),
    ],
  },

  // ===================================================================
  // 1803. HUBSPOT ENRICHMENT BATCH (7 nodes)
  // ===================================================================

  "HubSpot Enrichment Batch": {
    summary:
      "Runs daily to query incomplete HubSpot records, segments them into batches with runCode, enriches via Clearbit API, checks enrichment success rates via condition, falls back to secondary enrichment API, and batch-updates HubSpot records with an enrichment report in Sheets.",
    nodes: [
      trigger("Schedule: Daily HubSpot enrichment batch", "schedule", {
        schedule: "Every day at 5:00 AM",
        howItStarts:
          "Runs daily to find CRM contacts missing key data fields (company, title, industry, phone) and enriches them in batches using multiple data providers with automatic fallback",
        setup:
          "Configure HubSpot API token for record queries and updates. Set Clearbit API key as primary enrichment. Set Apollo API key as secondary fallback. Configure Google Sheets for enrichment reporting.",
        inputFields: [],
      }),

      // 2. Incomplete-record query HubSpot
      hubspotPostNode(
        "2",
        "Query Incomplete Records in HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"company","operator":"NOT_HAS_PROPERTY"}]},{"filters":[{"propertyName":"jobtitle","operator":"NOT_HAS_PROPERTY"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","jobtitle","phone","industry","city","state","lifecyclestage"]',
          },
          { key: "limit", value: "50" },
        ],
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "contact_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "contact_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "contact_last_name",
            type: "string",
          },
          { name: "total", customName: "total_incomplete", type: "number" },
        ],
      ),

      // 3. Batch segmentation runCode
      n("3", "runCode", "Segment Records into Enrichment Batches", 520, {
        runCodeDetails: {
          code: `const totalIncomplete = parseInt(variables.total_incomplete || '0');
const batchSize = 25;
const totalBatches = Math.ceil(totalIncomplete / batchSize);
const currentBatch = 1;
const email = variables.contact_email || '';
const domain = email.split('@')[1] || '';
const isPersonalEmail = ['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com'].includes(domain);
const enrichmentPriority = isPersonalEmail ? 'low' : 'high';
output.total_to_enrich = totalIncomplete;
output.batch_count = totalBatches;
output.current_batch = currentBatch;
output.email_domain = domain;
output.is_personal_email = isPersonalEmail;
output.enrichment_priority = enrichmentPriority;
output.batch_summary = 'Batch ' + currentBatch + '/' + totalBatches + ': ' + totalIncomplete + ' records. Domain: ' + domain + ' (' + enrichmentPriority + ' priority)';`,
          outputVariables: [
            { name: "total_to_enrich", type: "number" },
            { name: "batch_count", type: "number" },
            { name: "current_batch", type: "number" },
            { name: "email_domain", type: "string" },
            { name: "is_personal_email", type: "boolean" },
            { name: "enrichment_priority", type: "string" },
            { name: "batch_summary", type: "string" },
          ],
        },
      }),

      // 4. Clearbit batch enrichment API
      n("4", "hitApi", "Enrich Contact via Clearbit API", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/combined/find?email={contact_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "person.name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            {
              name: "person.title",
              customName: "enriched_title",
              type: "string",
            },
            {
              name: "person.role",
              customName: "enriched_role",
              type: "string",
            },
            {
              name: "person.seniority",
              customName: "enriched_seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "enriched_industry",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "enriched_employees",
              type: "string",
            },
            {
              name: "company.geo.city",
              customName: "enriched_city",
              type: "string",
            },
            {
              name: "company.geo.state",
              customName: "enriched_state",
              type: "string",
            },
          ],
        },
      }),

      // 5. Success-rate check condition
      n("5", "condition", "Check if Primary Enrichment Succeeded", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "enriched_company", operator: "not_equals", value: "" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Secondary enrichment API (fallback via output_2)
      n("6", "hitApi", "Fallback Enrichment via Apollo API", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/match",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "email", value: "{contact_email}" },
          ],
          variables: [
            {
              name: "person.name",
              customName: "fallback_name",
              type: "string",
            },
            {
              name: "person.title",
              customName: "fallback_title",
              type: "string",
            },
            {
              name: "person.organization.name",
              customName: "fallback_company",
              type: "string",
            },
            {
              name: "person.organization.industry",
              customName: "fallback_industry",
              type: "string",
            },
            {
              name: "person.city",
              customName: "fallback_city",
              type: "string",
            },
            {
              name: "person.state",
              customName: "fallback_state",
              type: "string",
            },
          ],
        },
      }),

      // 7. HubSpot batch update + enrichment report Sheets
      n("7", "hitApi", "Update HubSpot Contact with Enriched Data", 1480, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"company":"{enriched_company}","jobtitle":"{enriched_title}","industry":"{enriched_industry}","numberofemployees":"{enriched_employees}","city":"{enriched_city}","state":"{enriched_state}","notes_last_updated":"Auto-enriched via batch pipeline. Source: Clearbit. Role: {enriched_role}. Seniority: {enriched_seniority}. Batch: {current_batch}/{batch_count}."}',
            },
          ],
          variables: [
            { name: "id", customName: "updated_contact_id", type: "string" },
            {
              name: "properties.company",
              customName: "confirmed_company",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1804. IMPLEMENTATION MULTI-THREAD KICKOFF (9 nodes)
  // ===================================================================

  "Implementation Multi-Thread Kickoff": {
    summary:
      "Fires when a deal is marked closed-won via webhook, maps stakeholders with runCode, identifies gaps with runCode, discovers missing stakeholders via API, generates role-specific kickoff content with runCode, creates the project in Asana API, dispatches welcome emails, creates a Slack channel via API, and marks CRM handoff complete in HubSpot.",
    nodes: [
      trigger("Webhook: Deal closed-won in CRM", "webhook_app", {
        source: "hubspot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a deal stage changes to closed-won, triggering the multi-threaded implementation kickoff that onboards all stakeholders simultaneously across email, project management, and chat",
        setup:
          "Configure HubSpot webhook for deal stage changes to closed-won. Set OpenAI API key for content generation. Configure Apollo API for contact discovery. Set Asana API token for project creation. Configure Slack API token for channel creation. Set email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract closed-won deal data
      aiNode(
        "2",
        "AI Extract Closed-Won Deal Data",
        280,
        "You are a CRM deal event parser. Extract the deal ID, deal name, deal amount, product line, contract term months, account name, account domain, primary contact name, primary contact email, AE name, AE email, close date, and implementation notes from the webhook. Respond JSON: {deal_id, deal_name, deal_amount, product_line, contract_months, account_name, account_domain, primary_name, primary_email, ae_name, ae_email, close_date, implementation_notes}",
        "Raw webhook payload: {webhook_payload}. Extract all closed-won deal and account data regardless of field naming conventions.",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "product_line", as: "product_line", type: "string" },
          { field: "contract_months", as: "contract_months", type: "string" },
          { field: "account_name", as: "account_name", type: "string" },
          { field: "account_domain", as: "account_domain", type: "string" },
          { field: "primary_name", as: "primary_contact_name", type: "string" },
          {
            field: "primary_email",
            as: "primary_contact_email",
            type: "string",
          },
          { field: "ae_name", as: "ae_name", type: "string" },
          { field: "ae_email", as: "ae_email", type: "string" },
          { field: "close_date", as: "close_date", type: "string" },
          { field: "implementation_notes", as: "impl_notes", type: "string" },
        ],
      ),

      // 3. Stakeholder mapping runCode
      n("3", "runCode", "Map Implementation Stakeholder Roles", 520, {
        runCodeDetails: {
          code: `const dealAmount = parseInt(variables.deal_amount || '0');
const product = (variables.product_line || '').toLowerCase();
const contractMonths = parseInt(variables.contract_months || '12');
const requiredRoles = ['Executive Sponsor', 'Project Lead', 'Technical Admin'];
if (dealAmount >= 100000) requiredRoles.push('IT Security', 'Data Migration Lead');
if (product.includes('api') || product.includes('integration')) requiredRoles.push('API Integration Engineer');
if (contractMonths >= 24) requiredRoles.push('Long-Term Success Manager');
const kickoffTimeline = dealAmount >= 200000 ? '1 week' : dealAmount >= 50000 ? '2 weeks' : '3 weeks';
const complexityLevel = dealAmount >= 200000 ? 'enterprise' : dealAmount >= 50000 ? 'mid-market' : 'standard';
output.required_roles = requiredRoles.join(', ');
output.role_count = requiredRoles.length;
output.kickoff_timeline = kickoffTimeline;
output.complexity_level = complexityLevel;
output.stakeholder_plan = 'Implementation for ' + variables.account_name + ': ' + requiredRoles.length + ' roles needed. Complexity: ' + complexityLevel + '. Timeline: ' + kickoffTimeline;`,
          outputVariables: [
            { name: "required_roles", type: "string" },
            { name: "role_count", type: "number" },
            { name: "kickoff_timeline", type: "string" },
            { name: "complexity_level", type: "string" },
            { name: "stakeholder_plan", type: "string" },
          ],
        },
      }),

      // 4. Gap analysis + missing-stakeholder discovery API
      n("4", "hitApi", "Discover Implementation Stakeholders", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "q_organization_domains", value: "{account_domain}" },
            {
              key: "person_titles",
              value:
                '["IT Director","CTO","VP Engineering","Project Manager","IT Manager","Security Director"]',
            },
            { key: "per_page", value: "5" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "impl_contact_name",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "impl_contact_email",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "impl_contact_title",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "impl_contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 5. Role-specific kickoff content runCode
      n("5", "runCode", "Generate Role-Specific Kickoff Content", 1000, {
        runCodeDetails: {
          code: `const complexity = variables.complexity_level || 'standard';
const product = variables.product_line || 'Platform';
const timeline = variables.kickoff_timeline || '2 weeks';
const accountName = variables.account_name || '';
let milestones = [];
if (complexity === 'enterprise') {
  milestones = ['Week 1: Kickoff calls + access provisioning', 'Week 2: Data migration planning', 'Week 3-4: Integration setup + testing', 'Week 5-6: User training + UAT', 'Week 7-8: Go-live + hypercare'];
} else if (complexity === 'mid-market') {
  milestones = ['Week 1: Kickoff + access setup', 'Week 2: Configuration + data import', 'Week 3: Training + testing', 'Week 4: Go-live + support'];
} else {
  milestones = ['Day 1-3: Setup + configuration', 'Day 4-7: Training', 'Day 8-10: Go-live'];
}
const welcomeMessage = 'Welcome to the ' + product + ' implementation, ' + accountName + '! We are excited to partner with you on this journey. Your dedicated implementation team is ready to get started.';
output.milestone_plan = milestones.join(' | ');
output.welcome_message = welcomeMessage;
output.kickoff_agenda = '1. Introductions 2. Project scope review 3. Timeline alignment 4. Access provisioning 5. Communication channels 6. Q&A';
output.project_name = accountName + ' — ' + product + ' Implementation';`,
          outputVariables: [
            { name: "milestone_plan", type: "string" },
            { name: "welcome_message", type: "string" },
            { name: "kickoff_agenda", type: "string" },
            { name: "project_name", type: "string" },
          ],
        },
      }),

      // 6. Project setup Asana API
      n("6", "hitApi", "Create Implementation Project in Asana", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/projects",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"{project_name}","workspace":"{asana_workspace_id}","notes":"Deal: {deal_name} (${deal_amount}). Product: {product_line}. Contract: {contract_months} months. Complexity: {complexity_level}. Timeline: {kickoff_timeline}. Milestones: {milestone_plan}. Primary contact: {primary_contact_name} ({primary_contact_email}). AE: {ae_name}.","default_view":"board"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "asana_project_id",
              type: "string",
            },
            {
              name: "data.permalink_url",
              customName: "asana_project_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. Welcome email dispatch
      emailNode(
        "7",
        "Send Welcome Email to Primary Contact",
        1480,
        "{primary_contact_email}",
        "Welcome to {product_line} — Your Implementation Kickoff with {account_name}",
        "{welcome_message}\\n\\nHere is what to expect next:\\n\\n{milestone_plan}\\n\\nYour kickoff agenda:\\n{kickoff_agenda}\\n\\nProject dashboard: {asana_project_url}\\n\\nYour implementation team will be reaching out within {kickoff_timeline} to schedule the kickoff call.\\n\\nPlease do not hesitate to reach out with any questions.\\n\\nBest regards,\\nImplementation Team",
      ),

      // 8. Slack channel creation API
      n("8", "hitApi", "Create Dedicated Slack Channel", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/conversations.create",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "impl-{account_name}" },
            { key: "is_private", value: "true" },
          ],
          variables: [
            {
              name: "channel.id",
              customName: "slack_channel_id",
              type: "string",
            },
            {
              name: "channel.name",
              customName: "slack_channel_name",
              type: "string",
            },
          ],
        },
      }),

      // 9. CRM handoff completion HubSpot
      hubspotPostNode(
        "9",
        "Mark CRM Handoff Complete in HubSpot",
        1960,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Implementation kickoff completed. Asana project: {asana_project_url}. Slack channel: {slack_channel_name}. Complexity: {complexity_level}. Timeline: {kickoff_timeline}. Roles: {required_roles}. Welcome email sent to {primary_contact_name}. Implementation contacts found: {impl_contacts_found}.","hs_next_step":"Kickoff call within {kickoff_timeline}"}',
          },
        ],
        [
          { name: "id", customName: "handoff_deal_id", type: "string" },
          {
            name: "properties.hs_next_step",
            customName: "logged_next_step",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1805. INBOUND LEAD ENRICHMENT (8 nodes)
  // ===================================================================

  "Inbound Lead Enrichment": {
    summary:
      "Fires when a form is captured via webhook, enriches the company via Clearbit API, enriches the person via API, validates the email via API, scores the lead with runCode, routes by score via condition, creates the CRM record in HubSpot, and sends a routing notification via Slack and email.",
    nodes: [
      trigger("Webhook: Inbound form submission captured", "webhook_form", {
        source: "website",
        event: "form.submitted",
        howItStarts:
          "Fires when a prospect submits an inbound form (demo request, contact us, content download), sending the raw form fields and metadata for enrichment and routing",
        setup:
          "Configure form webhook for submission events. Set Clearbit API key for company enrichment. Set Apollo API key for person enrichment. Set email validation API key. Set HubSpot token for lead creation. Configure Slack webhook for routing notifications.",
        inputFields: [],
      }),

      // 2. AI extract form data
      aiNode(
        "2",
        "AI Extract Form Submission Data",
        280,
        "You are a web form data parser. Extract the submitter email, first name, last name, company name, phone, job title, form type (demo, contact, content), message, UTM source, UTM campaign, page URL, and any other form fields from the raw webhook. Respond JSON: {email, first_name, last_name, company, phone, job_title, form_type, message, utm_source, utm_campaign, page_url, referrer}",
        "Raw webhook payload: {webhook_payload}. Extract all form submission data regardless of field naming conventions or form builder used.",
        [
          { field: "email", as: "lead_email", type: "string" },
          { field: "first_name", as: "lead_first_name", type: "string" },
          { field: "last_name", as: "lead_last_name", type: "string" },
          { field: "company", as: "lead_company", type: "string" },
          { field: "phone", as: "lead_phone", type: "string" },
          { field: "job_title", as: "lead_title", type: "string" },
          { field: "form_type", as: "form_type", type: "string" },
          { field: "message", as: "lead_message", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "page_url", as: "page_url", type: "string" },
        ],
      ),

      // 3. Company research Clearbit API
      n("3", "hitApi", "Enrich Company via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/combined/find?email={lead_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
            { name: "company.tech", customName: "tech_stack", type: "string" },
            {
              name: "company.geo.country",
              customName: "company_country",
              type: "string",
            },
          ],
        },
      }),

      // 4. Person enrichment API
      n("4", "hitApi", "Enrich Person via Apollo", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/match",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "email", value: "{lead_email}" },
          ],
          variables: [
            {
              name: "person.title",
              customName: "enriched_title",
              type: "string",
            },
            {
              name: "person.seniority",
              customName: "enriched_seniority",
              type: "string",
            },
            {
              name: "person.linkedin_url",
              customName: "enriched_linkedin",
              type: "string",
            },
            {
              name: "person.departments",
              customName: "enriched_department",
              type: "string",
            },
          ],
        },
      }),

      // 5. Email validation API
      n("5", "hitApi", "Validate Lead Email Address", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={lead_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "status",
              customName: "email_valid_status",
              type: "string",
            },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
            { name: "free_email", customName: "is_free_email", type: "string" },
          ],
        },
      }),

      // 6. Lead scoring runCode
      n("6", "runCode", "Score Inbound Lead Based on Enrichment Data", 1240, {
        runCodeDetails: {
          code: `let score = 0;
const employees = parseInt(variables.company_employees || '0');
const revenue = parseInt(variables.company_revenue || '0');
const seniority = (variables.enriched_seniority || '').toLowerCase();
const formType = (variables.form_type || '').toLowerCase();
const emailValid = variables.email_valid_status === 'valid';
const isFree = variables.is_free_email === 'true';
const industry = (variables.company_industry || '').toLowerCase();
if (employees >= 100 && employees <= 5000) score += 20;
else if (employees > 5000) score += 15;
else if (employees >= 20) score += 10;
if (revenue >= 10000000) score += 15;
else if (revenue >= 1000000) score += 10;
if (seniority === 'director' || seniority === 'vp' || seniority === 'c_suite') score += 25;
else if (seniority === 'manager') score += 15;
else score += 5;
if (formType.includes('demo')) score += 20;
else if (formType.includes('pricing') || formType.includes('contact')) score += 15;
else if (formType.includes('content') || formType.includes('download')) score += 5;
if (emailValid) score += 5;
if (!isFree) score += 5;
if (industry.match(/technology|software|saas|fintech/)) score += 5;
const tier = score >= 70 ? 'hot' : score >= 45 ? 'warm' : score >= 25 ? 'nurture' : 'cold';
const sla = tier === 'hot' ? '1 hour' : tier === 'warm' ? '4 hours' : '24 hours';
output.lead_score = score;
output.lead_tier = tier;
output.response_sla = sla;
output.scoring_details = 'Score: ' + score + ' | Tier: ' + tier + ' | Employees: ' + employees + ' | Seniority: ' + seniority + ' | Form: ' + formType + ' | Email valid: ' + emailValid;`,
          outputVariables: [
            { name: "lead_score", type: "number" },
            { name: "lead_tier", type: "string" },
            { name: "response_sla", type: "string" },
            { name: "scoring_details", type: "string" },
          ],
        },
      }),

      // 7. Score-based routing condition
      n("7", "condition", "Route Lead Based on Score Tier", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "lead_tier", operator: "equals", value: "hot" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. CRM creation HubSpot + Slack notification
      hubspotPostNode(
        "8",
        "Create Enriched Lead in HubSpot",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{lead_email}","firstname":"{lead_first_name}","lastname":"{lead_last_name}","company":"{enriched_company}","jobtitle":"{enriched_title}","phone":"{lead_phone}","industry":"{company_industry}","numberofemployees":"{company_employees}","annualrevenue":"{company_revenue}","hs_linkedin_url":"{enriched_linkedin}","country":"{company_country}","lifecyclestage":"marketingqualifiedlead","hs_lead_status":"{lead_tier}","hs_analytics_source":"{utm_source}","notes_last_updated":"Inbound {form_type} lead. Score: {lead_score} (Tier: {lead_tier}). SLA: {response_sla}. Seniority: {enriched_seniority}. Department: {enriched_department}. Message: {lead_message}. Page: {page_url}. Campaign: {utm_campaign}."}',
          },
        ],
        [
          { name: "id", customName: "created_lead_id", type: "string" },
          {
            name: "properties.email",
            customName: "confirmed_lead_email",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1806. INDEED JOB-POSTING ENRICHMENT (8 nodes)
  // ===================================================================

  "Indeed Job-Posting Enrichment": {
    summary:
      "Runs daily to ingest recent job posts via API, extracts hiring signal data with runCode, AI-analyzes hiring intent and pain points, enriches the company via Clearbit API, assesses ICP fit via condition, discovers decision makers via API, and triggers outreach email plus CRM entry in HubSpot.",
    nodes: [
      trigger("Schedule: Daily Indeed job-posting scan", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to ingest new job postings from Indeed matching target keywords, extracting hiring signals that indicate companies may need your product and identifying decision makers for timely outreach",
        setup:
          "Configure Indeed/job board API key for posting ingestion. Set OpenAI API key for hiring-signal analysis. Set Clearbit API key for company enrichment. Set Apollo API key for contact discovery. Set HubSpot token for CRM entry. Set email sending credentials.",
        inputFields: [],
      }),

      // 2. Job-post ingestion API
      n("2", "hitApi", "Ingest Recent Job Postings from Indeed API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.indeed.com/ads/apisearch?publisher={indeed_publisher_id}&q={target_job_keywords}&l=&sort=date&radius=&st=&jt=&start=0&limit=25&fromage=1&co=us&v=2&format=json",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "results[0].jobtitle",
              customName: "job_title",
              type: "string",
            },
            {
              name: "results[0].company",
              customName: "hiring_company",
              type: "string",
            },
            {
              name: "results[0].formattedLocation",
              customName: "job_location",
              type: "string",
            },
            {
              name: "results[0].snippet",
              customName: "job_snippet",
              type: "string",
            },
            { name: "results[0].url", customName: "job_url", type: "string" },
            {
              name: "results[0].date",
              customName: "posting_date",
              type: "string",
            },
            {
              name: "results[0].formattedRelativeTime",
              customName: "posted_ago",
              type: "string",
            },
            {
              name: "totalResults",
              customName: "total_jobs_found",
              type: "number",
            },
          ],
        },
      }),

      // 3. Data extraction runCode
      n("3", "runCode", "Extract Hiring Signals from Job Posting", 520, {
        runCodeDetails: {
          code: `const title = (variables.job_title || '').toLowerCase();
const snippet = (variables.job_snippet || '').toLowerCase();
const company = variables.hiring_company || '';
const location = variables.job_location || '';
const signals = [];
if (title.match(/head of|director|vp|lead/)) signals.push('leadership_hire');
if (title.match(/first|founding/)) signals.push('new_team_build');
if (snippet.includes('scaling') || snippet.includes('growth')) signals.push('rapid_growth');
if (snippet.includes('replace') || snippet.includes('migration')) signals.push('tool_replacement');
if (snippet.includes('implement') || snippet.includes('roll out')) signals.push('new_implementation');
const hiringUrgency = snippet.includes('asap') || snippet.includes('immediate') ? 'urgent' : snippet.includes('start date') ? 'planned' : 'standard';
const budgetSignal = title.includes('senior') || title.includes('director') ? 'high_budget' : 'standard_budget';
output.hiring_signals = signals.join(', ') || 'general_hiring';
output.signal_count = signals.length;
output.hiring_urgency = hiringUrgency;
output.budget_signal = budgetSignal;
output.company_clean = company;
output.location_clean = location;
output.posting_summary = company + ' hiring ' + variables.job_title + ' in ' + location + '. Signals: ' + signals.join(', ');`,
          outputVariables: [
            { name: "hiring_signals", type: "string" },
            { name: "signal_count", type: "number" },
            { name: "hiring_urgency", type: "string" },
            { name: "budget_signal", type: "string" },
            { name: "company_clean", type: "string" },
            { name: "location_clean", type: "string" },
            { name: "posting_summary", type: "string" },
          ],
        },
      }),

      // 4. AI hiring-signal analysis
      aiNode(
        "4",
        "AI Analyze Hiring Intent and Pain Points",
        760,
        "You are a B2B sales intelligence analyst specializing in hiring-signal interpretation. Analyze the job posting to identify the company's likely pain points, technology needs, budget indicators, and sales opportunity angles. Return JSON: {pain_points, technology_needs, budget_indicator, opportunity_angle, outreach_timing, competitive_context, pitch_hook, confidence_level}",
        "Company: {company_clean} | Role: {job_title} | Location: {location_clean} | Signals: {hiring_signals} | Urgency: {hiring_urgency} | Budget: {budget_signal} | Snippet: {job_snippet}",
        [
          { field: "pain_points", as: "company_pain_points", type: "string" },
          { field: "technology_needs", as: "tech_needs", type: "string" },
          { field: "budget_indicator", as: "budget_indicator", type: "string" },
          {
            field: "opportunity_angle",
            as: "opportunity_angle",
            type: "string",
          },
          { field: "outreach_timing", as: "outreach_timing", type: "string" },
          { field: "pitch_hook", as: "pitch_hook", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
        ],
      ),

      // 5. Company enrichment Clearbit API
      n("5", "hitApi", "Enrich Hiring Company via Clearbit", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?name={company_clean}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            { name: "domain", customName: "company_domain", type: "string" },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "annual_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            { name: "tech", customName: "current_tech_stack", type: "string" },
          ],
        },
      }),

      // 6. ICP fit assessment condition
      n("6", "condition", "Assess ICP Fit for Outreach", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "signal_count", operator: "greater_than", value: "0" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Decision-maker discovery API
      n("7", "hitApi", "Discover Decision Makers at Company", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "q_organization_domains", value: "{company_domain}" },
            {
              key: "person_titles",
              value: '["VP","Director","Head","CTO","COO","CEO"]',
            },
            { key: "per_page", value: "3" },
          ],
          variables: [
            { name: "people[0].name", customName: "dm_name", type: "string" },
            { name: "people[0].email", customName: "dm_email", type: "string" },
            { name: "people[0].title", customName: "dm_title", type: "string" },
            {
              name: "people[0].linkedin_url",
              customName: "dm_linkedin",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "dm_found",
              type: "number",
            },
          ],
        },
      }),

      // 8. Outreach email + CRM entry HubSpot
      hubspotPostNode(
        "8",
        "Create CRM Entry and Trigger Outreach",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{dm_email}","firstname":"{dm_name}","company":"{enriched_company_name}","jobtitle":"{dm_title}","industry":"{company_industry}","numberofemployees":"{employee_count}","hs_linkedin_url":"{dm_linkedin}","lifecyclestage":"lead","hs_lead_status":"NEW","notes_last_updated":"Indeed hiring signal: {posting_summary}. Pain points: {company_pain_points}. Tech needs: {tech_needs}. Pitch hook: {pitch_hook}. Opportunity: {opportunity_angle}. Timing: {outreach_timing}. Confidence: {confidence_level}. Job URL: {job_url}."}',
          },
        ],
        [
          { name: "id", customName: "created_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "confirmed_dm_email",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1807. INDEED JOBS TO EMAIL QUEUE (8 nodes)
  // ===================================================================

  "Indeed Jobs to Email Queue": {
    summary:
      "Runs daily to monitor job postings via API, extracts hiring manager names with runCode, discovers email addresses via API, validates emails via API, AI-drafts personalized outreach tied to the job posting, optimizes send timing with runCode, and queues emails for delivery plus creates CRM records in HubSpot.",
    nodes: [
      trigger("Schedule: Daily job-to-outreach pipeline", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs daily to monitor new job postings matching target criteria, extract hiring manager contacts, and build a personalized email queue for timely outreach tied to specific job openings",
        setup:
          "Configure job board API key for posting monitoring. Set Apollo API key for contact discovery. Set email validation API key. Set OpenAI API key for outreach drafting. Set HubSpot token for CRM record creation.",
        inputFields: [],
      }),

      // 2. Job monitor API
      n("2", "hitApi", "Monitor New Job Postings via API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.indeed.com/ads/apisearch?publisher={indeed_publisher_id}&q={target_keywords}&sort=date&fromage=1&limit=20&format=json&v=2",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "results[0].jobtitle",
              customName: "posting_title",
              type: "string",
            },
            {
              name: "results[0].company",
              customName: "posting_company",
              type: "string",
            },
            {
              name: "results[0].formattedLocation",
              customName: "posting_location",
              type: "string",
            },
            {
              name: "results[0].snippet",
              customName: "posting_description",
              type: "string",
            },
            {
              name: "results[0].url",
              customName: "posting_url",
              type: "string",
            },
            {
              name: "results[0].date",
              customName: "posting_date",
              type: "string",
            },
            {
              name: "totalResults",
              customName: "total_postings",
              type: "number",
            },
          ],
        },
      }),

      // 3. Hiring-manager extraction runCode
      n("3", "runCode", "Extract Hiring Manager Signals from Posting", 520, {
        runCodeDetails: {
          code: `const title = (variables.posting_title || '').toLowerCase();
const company = variables.posting_company || '';
const description = (variables.posting_description || '').toLowerCase();
let targetTitles = [];
if (title.includes('engineer') || title.includes('developer')) {
  targetTitles = ['VP Engineering', 'Engineering Director', 'CTO', 'Engineering Manager'];
} else if (title.includes('sales') || title.includes('account')) {
  targetTitles = ['VP Sales', 'Sales Director', 'CRO', 'Head of Sales'];
} else if (title.includes('marketing')) {
  targetTitles = ['VP Marketing', 'Marketing Director', 'CMO', 'Head of Marketing'];
} else if (title.includes('product')) {
  targetTitles = ['VP Product', 'Product Director', 'CPO', 'Head of Product'];
} else {
  targetTitles = ['CEO', 'COO', 'Hiring Manager', 'HR Director'];
}
const department = title.includes('engineer') ? 'engineering' : title.includes('sales') ? 'sales' : title.includes('marketing') ? 'marketing' : title.includes('product') ? 'product' : 'general';
output.hiring_manager_titles = targetTitles.join(',');
output.department = department;
output.company_name = company;
output.role_context = 'Hiring for ' + variables.posting_title + ' in ' + department + ' at ' + company;
output.search_priority = title.includes('senior') || title.includes('lead') ? 'high' : 'standard';`,
          outputVariables: [
            { name: "hiring_manager_titles", type: "string" },
            { name: "department", type: "string" },
            { name: "company_name", type: "string" },
            { name: "role_context", type: "string" },
            { name: "search_priority", type: "string" },
          ],
        },
      }),

      // 4. Email discovery API
      n("4", "hitApi", "Discover Hiring Manager Email via Apollo", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "q_organization_name", value: "{company_name}" },
            { key: "person_titles", value: "{hiring_manager_titles}" },
            { key: "per_page", value: "3" },
          ],
          variables: [
            { name: "people[0].name", customName: "hm_name", type: "string" },
            { name: "people[0].email", customName: "hm_email", type: "string" },
            { name: "people[0].title", customName: "hm_title", type: "string" },
            {
              name: "people[0].linkedin_url",
              customName: "hm_linkedin",
              type: "string",
            },
            {
              name: "people[0].organization.name",
              customName: "hm_org",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "hm_contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 5. Email validation API
      n("5", "hitApi", "Validate Hiring Manager Email", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={hm_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "hm_email_status", type: "string" },
            {
              name: "sub_status",
              customName: "hm_email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. AI personalized outreach draft
      aiNode(
        "6",
        "AI Draft Job-Posting-Tied Outreach Email",
        1240,
        "You are a B2B sales copywriter who crafts outreach tied to specific hiring signals. Write a personalized email to the hiring manager referencing the specific job posting, connecting their hiring need to how your product can help. Be specific, not generic. Keep it under 130 words. Return JSON: {subject_line, email_body, opening_hook, value_connection, call_to_action, follow_up_variant}",
        "Hiring Manager: {hm_name} ({hm_title}) at {company_name} | Posting: {posting_title} in {posting_location} | Description: {posting_description} | Department: {department} | Context: {role_context} | Priority: {search_priority} | Posting URL: {posting_url}",
        [
          { field: "subject_line", as: "outreach_subject", type: "string" },
          { field: "email_body", as: "outreach_body", type: "string" },
          { field: "opening_hook", as: "opening_hook", type: "string" },
          { field: "value_connection", as: "value_connection", type: "string" },
          { field: "call_to_action", as: "outreach_cta", type: "string" },
          {
            field: "follow_up_variant",
            as: "followup_variant",
            type: "string",
          },
        ],
      ),

      // 7. Send-time optimization runCode
      n("7", "runCode", "Optimize Send Time for Maximum Open Rate", 1480, {
        runCodeDetails: {
          code: `const now = new Date();
const dayOfWeek = now.getDay();
const hour = now.getHours();
const priority = variables.search_priority || 'standard';
let optimalSendTime = '';
let sendDelay = 0;
if (dayOfWeek === 0) { sendDelay = 1; optimalSendTime = 'Monday 9:00 AM'; }
else if (dayOfWeek === 6) { sendDelay = 2; optimalSendTime = 'Monday 9:00 AM'; }
else if (dayOfWeek === 5 && hour >= 14) { sendDelay = 3; optimalSendTime = 'Monday 9:00 AM'; }
else if (hour < 9) { sendDelay = 0; optimalSendTime = 'Today 9:00 AM'; }
else if (hour >= 9 && hour <= 11) { sendDelay = 0; optimalSendTime = 'Now (peak morning window)'; }
else if (hour >= 14 && hour <= 16) { sendDelay = 0; optimalSendTime = 'Now (afternoon window)'; }
else { sendDelay = 1; optimalSendTime = 'Tomorrow 9:30 AM'; }
if (priority === 'high') sendDelay = Math.max(0, sendDelay - 1);
const scheduledDate = new Date(now.getTime() + sendDelay * 86400000).toISOString().split('T')[0];
output.optimal_send_time = optimalSendTime;
output.send_delay_days = sendDelay;
output.scheduled_date = scheduledDate;
output.send_strategy = 'Priority: ' + priority + ' | Send: ' + optimalSendTime + ' | Delay: ' + sendDelay + ' days';`,
          outputVariables: [
            { name: "optimal_send_time", type: "string" },
            { name: "send_delay_days", type: "number" },
            { name: "scheduled_date", type: "string" },
            { name: "send_strategy", type: "string" },
          ],
        },
      }),

      // 8. Email queue + CRM record HubSpot
      hubspotPostNode(
        "8",
        "Queue Email and Create CRM Record in HubSpot",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{hm_email}","firstname":"{hm_name}","company":"{hm_org}","jobtitle":"{hm_title}","hs_linkedin_url":"{hm_linkedin}","lifecyclestage":"lead","hs_lead_status":"NEW","notes_last_updated":"Indeed job signal: {role_context}. Posting: {posting_title} ({posting_url}). Send scheduled: {optimal_send_time} ({scheduled_date}). Priority: {search_priority}. Hook: {opening_hook}. Value: {value_connection}. Email status: {hm_email_status}. Contacts found: {hm_contacts_found}."}',
          },
        ],
        [
          { name: "id", customName: "queued_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "queued_email",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
